import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, withCellTemplates } from '../../shared/components/primeng';
import { FilterPanelComponent, FilterField } from '../../shared/components/filter-panel/filter-panel.component';
import { ActionDropdownTriggerDirective } from '../../shared/directives/action-dropdown-trigger.directive';
import { ProviderPreviewMetadataComponent } from '../../shared/components/contribution-steps/preview-metadata/preview-metadata.component';
import { CatalogueApiService, FileApiService, SearchCriteria, AuthApiService, UserInteractionApiService, type ProviderFeedbackItem } from '@angular-workspace/services';
import { AssetTypeMapperService } from '@angular-workspace/core';
import { ToasterService } from '../../services/toaster.service';
import { FormatService } from '../../shared/services/format.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ActionDropdownOverlayPosition } from '../../shared/utils/action-dropdown-position.util';
import { ActionDropdownOverlayController } from '../../shared/utils/action-dropdown-overlay.sync';
import { ratingStarFraction } from '../../shared/utils/user-feedback.util';
import {
  buildAssetCataloguePayloadWithAuth,
  normalizeAssetDescriptorList,
  resolveModifyAssetDescriptors,
} from '../../shared/utils/asset-catalogue-payload.util';
import {
  UploadFilesComponent,
  OgcUploadFilesComponent,
  StacCollectionOnboardingComponent,
  getConnectionDetailsModalLedeKind,
  normalizeResourceServerId,
  isSftpOrApiItemStatus,
  uploadMethodFromItemStatus,
  type ConnectionDetailsModalLedeKind,
  type NgsildOnboardingPayload,
  type NgsildUploadRequestType,
  type NgsildYearRange,
  type ResourceServerId,
  type VerifiedUploadRequestType,
  AdditionalInfoModalComponent,
  hasFaqOrInfoFeedback,
} from '@angular-workspace/feature';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';

export interface ContributionData {
  assetName: string;
  assetType: string;
  permission: string;
  resourceServer: string;
  publishedOn: string;
  /** Display label: Pending, Active, Rejected, etc. (provider Manage Assets). */
  status?: string;
  action: string;
  originalData?: any;
}

/** Active panel in the View Files modal. */
export type ViewFilesModalTab = 'upload' | 'files';

/** Row in the View Files tab (mapped from file-server list API). */
export interface DatasetFileRow {
  id: string;
  fileName: string;
  size: string;
  uploadedOn: string;
  status: string;
}

export interface AssetFeedbackRow {
  consumerName: string;
  consumerEmail: string;
  organisation: string;
  userFeedback: string;
  rating: string;
  ratingValue: number;
  createdAt: string;
}

export interface AssetFeedbackSummary {
  averageRating: number | null;
  totalRatings: number;
}

@Component({
  selector: 'app-org-admin-manage-contributions',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    FilterPanelComponent,
    ModalComponent,
    ProviderPreviewMetadataComponent,
    ActionDropdownTriggerDirective,
    UploadFilesComponent,
    OgcUploadFilesComponent,
    StacCollectionOnboardingComponent,
    ConfirmationDialogComponent,
    AdditionalInfoModalComponent,
  ],
  templateUrl: './manage-contributions.component.html',
  styleUrl: './manage-contributions.component.scss',
})
export class OrgAdminManageContributionsComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * Org-admin full page (default) vs provider shell: same Manage Assets UI & actions, provider uses my-assets API & upload routes.
   */
  @Input() manageAssetsPortal: 'org-admin' | 'provider' = 'org-admin';

  get isOrgAdminPortal(): boolean {
    return this.manageAssetsPortal === 'org-admin';
  }

  get isProviderPortal(): boolean {
    return this.manageAssetsPortal === 'provider';
  }

  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('permissionCell', { static: false }) permissionCellTemplate!: TemplateRef<any>;
  @ViewChild('resourceServerCell', { static: false }) resourceServerCellTemplate!: TemplateRef<any>;
  @ViewChild('publishedOnCell', { static: false }) publishedOnCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  /** View Files Actions column template (no Status column — fixed-right Action cell). */
  @ViewChild('viewFilesActionsCell', { static: true })
  viewFilesActionsCellTemplate!: TemplateRef<any>;

  /** Default sort when no query param or when Asset Type is selected (remove sort). */
  private readonly DEFAULT_SORT = 'itemCreatedAt:desc';

  /** Map table column key → API sort field. assetType → null means remove sort. */
  private readonly COLUMN_TO_API_SORT: Record<string, string | null> = {
    assetName: 'name',
    assetType: null,
    permission: 'accessPolicy',
    publishedOn: 'itemCreatedAt',
  };

  /** Map API sort field → table column key (for initial state from URL). */
  private readonly API_SORT_TO_COLUMN: Record<string, string> = {
    name: 'assetName',
    accessPolicy: 'permission',
    itemCreatedAt: 'publishedOn',
  };

  /** Current API sort string (e.g. 'itemCreatedAt:desc'). Used in loadContributions. */
  currentSortApi = this.DEFAULT_SORT;

  /** Initial sort for table header (from URL). */
  initialSortColumn: string | null = null;
  initialSortDirection: 'asc' | 'desc' = 'asc';

  private destroy$ = new Subject<void>();
  /** Skip next queryParams-triggered load when we just navigated from onSortChange. */
  private skipNextSortParamsLoad = false;
  /** Asset ids removed this session; filtered from reloads until search index catches up. */
  private readonly deletedAssetIds = new Set<string>();

  // Modal state for Modify Asset (same as Access Request page)
  showModifyModal = false;
  currentModifyAsset: ContributionData | null = null;
  modifyAssetData: any = null;
  /** Bumped when modify modal loads; forces preview form remount per asset. */
  modifyFormInstanceKey: string | null = null;
  isLoadingAssetData = false;
  /** Ignores stale getItemById responses when the user opens modify on another row. */
  private modifyAssetLoadToken = 0;
  /** Ensures a fresh preview-metadata instance on every modify open (including same asset). */
  private modifyFormInstanceSeq = 0;

  /** NGSI-LD / Gateway: same “Add Data” modal as dataset onboarding (`lib-ngsild-upload-files`). */
  showNgsildUploadModal = false;
  ngsildUploadModalRow: ContributionData | null = null;

  /** OGC Server: GeoPackage upload modal (`lib-ogc-upload-files`). */
  showOgcUploadModal = false;
  ogcUploadModalRow: ContributionData | null = null;
  ogcUploadLoadKey = 0;

  /** OGC Raster: STAC collection / item / asset onboarding modal (`lib-stac-collection-onboarding`). */
  showStacOnboardingModal = false;
  stacOnboardingModalRow: ContributionData | null = null;
  stacOnboardingCollectionExists = false;

  /** View Feedback modal for the selected asset. */
  showViewFeedbackModal = false;
  viewFeedbackAssetName = '';
  viewFeedbackRows: AssetFeedbackRow[] = [];
  viewFeedbackSummary: AssetFeedbackSummary | null = null;
  viewFeedbackLoading = false;
  viewFeedbackError: string | null = null;

  /** View Files modal (File resource server). */
  showFileUploadModal = false;
  fileUploadModalRow: ContributionData | null = null;
  viewFilesTableRows: DatasetFileRow[] = [];
  viewFilesModalTab: ViewFilesModalTab = 'files';
  /** File list tab loads via FileApiService.listFiles (`POST …/v1/databanks/{id}/files`). */
  viewFilesListLoading = false;
  viewFilesListError: string | null = null;

  /** View Files table: fixed overlay dropdown (same pattern as Manage Assets Action column). */
  viewFilesOpenDropdownKey: string | null = null;
  viewFilesDropdownOverlayPosition: ActionDropdownOverlayPosition | null = null;
  /** Row for the View Files action overlay (public for template binding). */
  viewFilesDropdownRow: DatasetFileRow | null = null;

  /** Additional Information modal (FAQ / INFO) opened from the Action menu. */
  showAdditionalInfoModal = false;
  additionalInfoModalRow: ContributionData | null = null;
  additionalInfoCache = new Map<string, ProviderFeedbackItem[]>();
  private readonly additionalInfoInflight = new Map<string, Promise<ProviderFeedbackItem[]>>();

  /** Delete file confirmation (same pattern as profile/account delete dialog). */
  showViewFilesDeleteConfirmDialog = false;
  viewFilesFilePendingDelete: DatasetFileRow | null = null;
  viewFilesDeleteLoading = false;

  /** Cached `requestStatus` from `/auth/asset/request` keyed by catalogue asset id (SFTP/API rows). */
  private readonly assetRequestStatusByAssetId = new Map<string, string>();

  /** After SFTP / API `asset/request` success: same UX as onboarding (full-screen confirmation). */
  ngsildUploadRequestSuccessVisible = false;
  ngsildUploadRequestSuccessType: NgsildUploadRequestType | null = null;
  /** Row context for “View dataset” on that success modal. */
  private ngsildUploadRequestSuccessRow: ContributionData | null = null;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private fileApiService: FileApiService,
    private authApiService: AuthApiService,
    private userInteractionApiService: UserInteractionApiService,
    private assetTypeMapper: AssetTypeMapperService,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private formatService: FormatService,
    private cdr: ChangeDetectorRef,
    private keycloak: KeycloakService
  ) {}

  columns: TableColumn[] = [];

  private readonly manageAssetsBaseColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: true },
    { key: 'assetType', label: 'Asset Type', sortable: false },
    { key: 'permission', label: 'Permission', sortable: true },
    { key: 'resourceServer', label: 'Resource Server', sortable: false, width: '160px' },
    { key: 'publishedOn', label: 'Published On', sortable: true },
  ];

  /** Columns for View Files modal — Actions template merged in {@link tryBindViewFilesTableTemplates}. */
  viewFilesTableColumns: TableColumn[] = [
    { key: 'fileName', label: 'File name', sortable: false },
    { key: 'size', label: 'Size', sortable: false },
    { key: 'uploadedOn', label: 'Uploaded on', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false, fixed: 'right', width: '140px', align: 'left' },
  ];

  readonly viewFilesTablePagination: PaginationConfig = {
    enabled: false,
    currentPage: 1,
    pageSize: 100,
    totalItems: 0,
    pageSizeOptions: [],
  };

  contributionsData: ContributionData[] = [];
  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  loading = false;
  emptyMessage = 'No contributions found';

  // Filter fields
  filterFields: FilterField[] = [
    {
      key: 'assetType',
      label: 'Asset Type',
      type: 'multiselect',
      placeholder: 'All',
      options: ['Dataset', 'Model'],
      value: [],
    },
    {
      key: 'search',
      label: 'Search Assets',
      type: 'text',
      placeholder: 'Search assets...',
      value: '',
    },
  ];

  // Filter values
  searchTerm: string = '';
  /** Empty = all types; otherwise filters by selected Dataset / Model values. */
  selectedAssetTypes: string[] = [];

  // Search debounce
  private searchSubject = new Subject<string>();
  private searchSubscription: any;
  private isInitialLoad = true;

  openDropdownId: string | null = null;
  /** Row captured when the action menu opens (avoids null after document-click / close). */
  private actionDropdownRow: ContributionData | null = null;
  /** `mediaURL` from `getItemById` — list/search responses often omit it. */
  private readonly assetMediaUrlByAssetId = new Map<string, string>();
  private readonly assetMediaUrlFetchInFlight = new Set<string>();
  /** Position for fixed overlay (dropdown above table). */
  overlayPosition: ActionDropdownOverlayPosition | null = null;
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();
  private readonly viewFilesActionDropdownOverlay = new ActionDropdownOverlayController();

  async ngOnInit(): Promise<void> {
    this.initColumnsForPortal();
    await this.waitForKeycloakReady();
    // Sync sort from URL (and on browser back/forward). Skip load when we just navigated from onSortChange.
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (this.skipNextSortParamsLoad) {
        this.skipNextSortParamsLoad = false;
        return;
      }
      this.applySortFromParams(params['sort'] ?? null);
      this.loadContributions();
    });

    // Setup search debounce - only trigger loadContributions for search changes
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchTerm = searchTerm;
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.loadContributions();
    });

    // Mark initial load as complete after a short delay to avoid filter panel init events
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);
  }

  /** Parse sort query param and set currentSortApi + table initial sort. */
  private applySortFromParams(sortParam: string | null): void {
    if (!sortParam || typeof sortParam !== 'string') {
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      return;
    }
    const [field, dir] = sortParam.split(':');
    const direction = (dir === 'asc' || dir === 'desc') ? dir : 'asc';
    const apiField = (field && field.trim()) || null;
    if (!apiField || !this.API_SORT_TO_COLUMN[apiField]) {
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      return;
    }
    this.currentSortApi = `${apiField}:${direction}`;
    this.initialSortColumn = this.API_SORT_TO_COLUMN[apiField];
    this.initialSortDirection = direction;
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    const apiField = this.COLUMN_TO_API_SORT[event.column];
    this.skipNextSortParamsLoad = true; // avoid duplicate load when subscription fires after navigate
    if (apiField == null) {
      // Asset Type: remove sort from URL and use default
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      this.loadContributions();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { sort: null },
        queryParamsHandling: 'merge',
      });
      return;
    }
    const sortValue = `${apiField}:${event.direction}`;
    this.currentSortApi = sortValue;
    this.initialSortColumn = event.column;
    this.initialSortDirection = event.direction;
    this.loadContributions();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: sortValue },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
    this.closeViewFilesActionDropdown();
    this.closeModifyModal();
    this.closeNgsildUploadModal();
    this.closeFileUploadModal();
    this.closeViewFeedbackModal();
    this.closeStacOnboardingModal();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000, checkInterval = 100, startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    const actionColumn = this.columns.find(col => col.key === 'action');
    if (actionColumn && this.actionCellTemplate) {
      actionColumn.cellTemplate = this.actionCellTemplate;
    }
    const permissionColumn = this.columns.find(col => col.key === 'permission');
    if (permissionColumn && this.permissionCellTemplate) {
      permissionColumn.cellTemplate = this.permissionCellTemplate;
    }
    const resourceServerColumn = this.columns.find(col => col.key === 'resourceServer');
    if (resourceServerColumn && this.resourceServerCellTemplate) {
      resourceServerColumn.cellTemplate = this.resourceServerCellTemplate;
    }
    const publishedOnColumn = this.columns.find(col => col.key === 'publishedOn');
    if (publishedOnColumn && this.publishedOnCellTemplate) {
      publishedOnColumn.cellTemplate = this.publishedOnCellTemplate;
    }
    const statusColumn = this.columns.find(col => col.key === 'status');
    if (statusColumn && this.statusCellTemplate) {
      statusColumn.cellTemplate = this.statusCellTemplate;
    }
    this.tryBindViewFilesTableTemplates();
  }

  /** Provider Manage Assets includes a Status column; org-admin table also includes it. */
  private initColumnsForPortal(): void {
    const actionColumn: TableColumn = {
      key: 'action',
      label: 'Action',
      sortable: false,
      fixed: 'right',
      width: '120px',
    };
    // Both org-admin and provider portals now show the Status column
    this.columns = [
      ...this.manageAssetsBaseColumns,
      { key: 'status', label: 'Status', sortable: false },
      actionColumn,
    ];
  }

  /** Assigns the Actions column cell template for the View Files modal table (immutable columns array). */
  private tryBindViewFilesTableTemplates(): void {
    const actionsTpl = this.viewFilesActionsCellTemplate;
    if (!actionsTpl) {
      return;
    }
    const baseCols: TableColumn[] = [
      { key: 'fileName', label: 'File name', sortable: false },
      { key: 'size', label: 'Size', sortable: false },
      { key: 'uploadedOn', label: 'Uploaded on', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false, fixed: 'right', width: '140px', align: 'left' },
    ];
    this.viewFilesTableColumns = withCellTemplates(baseCols, [{ key: 'actions', cellTemplate: actionsTpl }]);
    this.cdr.detectChanges();
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  /** Permission display label: OPEN/PUBLIC -> Public, RESTRICTED -> Restricted. */
  getPermissionLabel(permission: string): string {
    if (!permission) return 'Public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'Public';
    if (p === 'RESTRICTED') return 'Restricted';
    if (p === 'PRIVATE') return 'Private';
    return permission;
  }

  /** Badge wrapper class for permission column (icon + content, dataset collection style). */
  getPermissionBadgeClass(permission: string): string {
    if (!permission) return 'permission-badge';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'permission-badge permission-badge-public';
    if (p === 'RESTRICTED') return 'permission-badge permission-badge-restricted';
    if (p === 'PRIVATE') return 'permission-badge permission-badge-private';
    return 'permission-badge';
  }

  /** CSS class for permission chip. */
  getPermissionChipClass(permission: string): string {
    if (!permission) return 'contribution-chip contribution-chip--public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'contribution-chip contribution-chip--public';
    if (p === 'RESTRICTED') return 'contribution-chip contribution-chip--restricted';
    if (p === 'PRIVATE') return 'contribution-chip contribution-chip--private';
    return 'contribution-chip';
  }

  private readonly IST_OPTIONS: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  /** Date part of Published On in IST (e.g. 06-Feb-2026). */
  getPublishedOnDatePart(dateValue: string): string {
    if (!dateValue) return '—';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue;
    const parts = new Intl.DateTimeFormat('en-GB', this.IST_OPTIONS).formatToParts(d);
    const day = parts.find(p => p.type === 'day')?.value ?? '00';
    const month = parts.find(p => p.type === 'month')?.value ?? '';
    const year = parts.find(p => p.type === 'year')?.value ?? '';
    return `${day}-${month}-${year}`;
  }

  /** Time part of Published On in IST (e.g. 07:58). Styled grey, smaller font in template. */
  getPublishedOnTimePart(dateValue: string): string {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-GB', this.IST_OPTIONS).formatToParts(d);
    const hour = (parts.find(p => p.type === 'hour')?.value ?? '00').padStart(2, '0');
    const minute = (parts.find(p => p.type === 'minute')?.value ?? '00').padStart(2, '0');
    return ` ${hour}:${minute}`;
  }

  /** Format Published On as DD-MMM-YYYY HH:MM in IST (e.g. 06-Feb-2026 07:58). */
  formatPublishedOn(dateValue: string): string {
    return (this.getPublishedOnDatePart(dateValue) + this.getPublishedOnTimePart(dateValue)).trim() || '—';
  }

  /** Icon variant for table cell (matches dataset onboarding cards). */
  getResourceServerIconType(
    label: string | null | undefined,
  ): 'network' | 'files' | 'globe' | 'server' {
    const id = normalizeResourceServerId(label ?? '');
    switch (id) {
      case 'file-server':
        return 'files';
      case 'gateway':
        return 'server';
      case 'ogc':
        return 'globe';
      default:
        return 'network';
    }
  }

  /** Primary resource server label from API (`resourceServer[0].name`, e.g. NGSI-LD). */
  getResourceServerNameFromItem(item: Record<string, unknown> | null | undefined): string {
    if (!item) return '—';
    const rs = item['resourceServer'] ?? item['resource_server'];
    if (Array.isArray(rs) && rs.length > 0) {
      const first = rs[0];
      if (first && typeof first === 'object' && !Array.isArray(first)) {
        const name = (first as Record<string, unknown>)['name'];
        if (name != null && String(name).trim()) {
          return String(name).trim();
        }
      }
    }
    if (rs && typeof rs === 'object' && !Array.isArray(rs)) {
      const name = (rs as Record<string, unknown>)['name'];
      if (name != null && String(name).trim()) {
        return String(name).trim();
      }
    }
    return '—';
  }

  /** Normalize API item to ContributionData; handles type as string or array (e.g. ["adex:AiModel"]). */
  private mapItemToContribution(item: any): ContributionData {
    const rawType = item.type ?? item.assetType ?? item.asset_type ?? '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    const permissionRaw = item.permission || item.accessPolicy || item.access_policy || 'PUBLIC';
    const itemStatus = this.getItemStatusFromApiItem(item);
    const status = this.resolveContributionStatus(item, itemStatus);
    return {
      assetName: item.title || item.name || item.assetName || item.asset_name || '',
      assetType: this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr,
      permission: permissionRaw,
      resourceServer: this.getResourceServerNameFromItem(item),
      publishedOn: item.itemCreatedAt || item.item_created_at || item.createdAt || item.created_at || new Date().toISOString(),
      status,
      action: '',
      originalData: item
    };
  }

  private getItemStatusFromApiItem(item: Record<string, unknown> | null | undefined): string {
    if (!item) return '';
    const status = item['itemStatus'] ?? item['item_status'] ?? '';
    return String(status).trim().toUpperCase();
  }

  private isSftpOrApiItemStatus(itemStatus: string): boolean {
    return isSftpOrApiItemStatus(itemStatus);
  }

  private isDirectItemStatus(itemStatus: string): boolean {
    return itemStatus === 'DIRECT';
  }

  /** Table status: ACTIVE catalogue rows use publish + data upload; SFTP/API rows use asset/request status. */
  private resolveContributionStatus(
    item: Record<string, unknown>,
    itemStatus: string,
  ): string {
    if (this.isSftpOrApiItemStatus(itemStatus)) {
      const assetId = String(item['id'] ?? item['assetId'] ?? item['asset_id'] ?? '').trim();
      const cached = assetId ? this.assetRequestStatusByAssetId.get(assetId) : undefined;
      return this.resolveSftpApiRowStatus(item, cached);
    }

    const statusRaw = item['publishStatus'] ?? item['publish_status'] ?? item['status'] ?? '';
    return this.resolveAssetTableStatus(String(statusRaw), item);
  }

  private isPublishActiveFromItem(item: Record<string, unknown>): boolean {
    const publishStatus = String(item['publishStatus'] ?? item['publish_status'] ?? '')
      .trim()
      .toUpperCase();
    return (
      publishStatus === 'ACTIVE' ||
      publishStatus === 'GRANTED' ||
      publishStatus === 'PUBLISHED' ||
      publishStatus === 'LIVE'
    );
  }

  private isPublishRejectedFromItem(item: Record<string, unknown>): boolean {
    const publishStatus = String(
      item['publishStatus'] ?? item['publish_status'] ?? item['status'] ?? '',
    )
      .trim()
      .toUpperCase();
    return (
      publishStatus === 'DECLINED' ||
      publishStatus === 'REJECTED' ||
      publishStatus === 'DENIED' ||
      publishStatus === 'FAILED'
    );
  }

  private isAssetRequestGranted(requestStatus: string | undefined): boolean {
    const status = String(requestStatus ?? '').trim().toUpperCase();
    return status === 'GRANTED' || status === 'APPROVED';
  }

  /**
   * SFTP/API rows with request GRANTED and publish ACTIVE use publish + dataUploadStatus
   * (Active/Pending). Granted with publish not Active also shows Pending.
   */
  private shouldUsePublishFlowForSftpApi(
    item: Record<string, unknown>,
    requestStatus: string | undefined,
  ): boolean {
    if (!requestStatus) return false;
    return this.isPublishActiveFromItem(item) && this.isAssetRequestGranted(requestStatus);
  }

  private resolveSftpApiRowStatus(
    item: Record<string, unknown>,
    requestStatus: string | undefined,
  ): string {
    if (this.isPublishRejectedFromItem(item)) {
      return 'Rejected';
    }

    const status = String(requestStatus ?? '').trim().toUpperCase();
    if (!status) return 'Pending';

    if (status === 'REJECTED') {
      return this.mapAssetRequestStatusDisplay(status);
    }

    if (this.isAssetRequestGranted(requestStatus)) {
      if (this.isPublishActiveFromItem(item)) {
        const statusRaw = item['publishStatus'] ?? item['publish_status'] ?? item['status'] ?? '';
        return this.resolveAssetTableStatus(String(statusRaw), item);
      }
      return 'Pending';
    }

    return this.mapAssetRequestStatusDisplay(status);
  }

  private mapAssetRequestStatusDisplay(raw: string | undefined): string {
    const value = String(raw ?? '').trim();
    if (!value) return 'Pending';
    return this.formatService.toPascalCase(value);
  }

  private extractRequestStatusFromAssetRequestResponse(response: unknown): string {
    const payload =
      response && typeof response === 'object'
        ? ((response as Record<string, unknown>)['data'] ?? response)
        : null;
    const resultArray = (payload as Record<string, unknown> | null)?.['result'];
    if (!Array.isArray(resultArray) || resultArray.length === 0) {
      return '';
    }
    const item = resultArray[0] as Record<string, unknown>;
    const req = (item?.['assetRequest'] as Record<string, unknown> | undefined) ?? item;
    return String(
      req?.['requestStatus'] ??
        req?.['request_status'] ??
        req?.['status'] ??
        item?.['requestStatus'] ??
        item?.['request_status'] ??
        item?.['status'] ??
        '',
    ).trim();
  }

  /** Fetch `/auth/asset/request` for SFTP/API catalogue rows and cache `requestStatus` for the table. */
  private async enrichAssetRequestStatuses(rows: ContributionData[]): Promise<void> {
    const tasks = rows
      .filter((row) => this.isSftpOrApiItemStatus(this.getItemStatus(row)))
      .map(async (row) => {
        const assetId = this.getRowAssetId(row);
        if (!assetId) return;
        const type = this.getItemStatus(row);
        try {
          const response = await this.authApiService.getAssetRequests(
            1,
            10,
            type,
            undefined,
            assetId,
          );
          const requestStatus = this.extractRequestStatusFromAssetRequestResponse(response);
          if (!requestStatus) return;
          this.assetRequestStatusByAssetId.set(assetId, requestStatus);
          const original = (row.originalData ?? {}) as Record<string, unknown>;
          row.status = this.resolveSftpApiRowStatus(original, requestStatus);
          row.originalData = {
            ...original,
            assetRequestStatus: requestStatus,
          };
        } catch (err: unknown) {
          console.warn(`asset/request status for ${assetId}:`, err);
        }
      });
    await Promise.allSettled(tasks);
  }

  private getDataUploadStatusFromItem(item: Record<string, unknown> | null | undefined): boolean | undefined {
    if (!item) return undefined;
    const raw = item['dataUploadStatus'] ?? item['data_upload_status'];
    if (raw === true || raw === 'true') return true;
    if (raw === false || raw === 'false') return false;
    return undefined;
  }

  /**
   * Table status: Active only when ALL three conditions are met:
   *   1. publishStatus is ACTIVE (Platform Admin approved)
   *   2. itemStatus is VERIFIED (Org Admin approved)
   *   3. dataUploadStatus is true (upload complete)
   * If platform admin has approved but org admin has not yet, show Pending.
   */
  private resolveAssetTableStatus(statusRaw: string, item: Record<string, unknown>): string {
    const publishStatus = String(statusRaw ?? '').trim().toUpperCase();
    const isPublishActive =
      publishStatus === 'ACTIVE' ||
      publishStatus === 'GRANTED' ||
      publishStatus === 'PUBLISHED' ||
      publishStatus === 'LIVE';

    if (isPublishActive) {
      const itemStatus = this.getItemStatusFromApiItem(item);
      const isOrgAdminApproved = itemStatus === 'VERIFIED' || itemStatus.startsWith('VERIFIED-');
      if (!isOrgAdminApproved) {
        return 'Pending';
      }
      return this.getDataUploadStatusFromItem(item) === true ? 'Active' : 'Pending';
    }

    return this.mapPublishStatusDisplay(statusRaw);
  }

  /**
   * Map API publish status to display label.
   * ACTIVE → Active, PENDING → Pending, DECLINED → Rejected.
   */
  private mapPublishStatusDisplay(raw: string): string {
    const s = String(raw ?? '').trim().toUpperCase();
    if (!s) return '—';
    if (s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'Active';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DECLINED' || s === 'REJECTED' || s === 'DENIED' || s === 'FAILED') return 'Rejected';
    if (s === 'DRAFT') return 'Draft';
    return String(raw).trim() || '—';
  }

  /** Status display label for the table (Pending, Active, Rejected, etc.). */
  getStatusLabel(status: string | undefined): string {
    if (!status) return '—';
    return this.mapPublishStatusDisplay(status);
  }

  private getAssetRequestStatusForRow(row: ContributionData): string {
    const assetId = this.getRowAssetId(row);
    const cached = assetId ? this.assetRequestStatusByAssetId.get(assetId) : undefined;
    const fromOriginal = row.originalData?.assetRequestStatus;
    return String(cached ?? fromOriginal ?? '').trim().toUpperCase();
  }

  /** True when catalogue row is SFTP/API and platform admin rejected the upload request. */
  shouldShowSftpApiRejectedInfoIcon(row: ContributionData): boolean {
    if (!this.isSftpOrApiItemStatus(this.getItemStatus(row))) {
      return false;
    }
    return this.getAssetRequestStatusForRow(row) === 'REJECTED';
  }

  getSftpApiRejectedInfoTooltip(row: ContributionData): string {
    if (this.isOrgAdminPublishRejected(row)) {
      return 'The publish request has been rejected by the Organization Admin. The asset will not be published.';
    }
    const type = this.getItemStatus(row);
    if (uploadMethodFromItemStatus(type) === 'API') {
      return 'API request rejected by Platform Administrator';
    }
    return 'SFTP request rejected by Platform Administrator';
  }

  /** Provider SFTP/API rows: show which approval stage is pending beside the Pending chip. */
  shouldShowSftpApiPendingApprovalHint(row: ContributionData): boolean {
    return this.getSftpApiPendingApprovalHintLabel(row) != null;
  }

  getSftpApiPendingApprovalHintLabel(row: ContributionData): string | null {
    if (!this.isProviderPortal) return null;
    if (!this.isSftpOrApiItemStatus(this.getItemStatus(row))) return null;
    if (String(this.getStatusLabelForRow(row)).trim().toLowerCase() !== 'pending') return null;

    const original = (row.originalData ?? {}) as Record<string, unknown>;
    const publishActive = this.isPublishActiveFromItem(original);
    const dataUpload = this.getDataUploadStatusFromItem(original);

    // Both not approved — only show the Pending chip, no bracket hint.
    if (!publishActive && dataUpload !== true) {
      return null;
    }
    if (!publishActive && dataUpload === true) {
      return 'Organisation admin approval pending';
    }
    if (publishActive && dataUpload !== true) {
      return 'Platform admin approval pending';
    }
    return null;
  }

  getSftpApiPendingApprovalHintTooltip(row: ContributionData): string {
    if (this.shouldShowUploadLaterPendingInfoIcon(row)) {
      return 'Publish request has been approved by the Organization Admin. The asset is not yet uploaded. The asset will be published once the upload is completed.';
    }
    return this.getSftpApiPendingApprovalHintLabel(row) ?? '';
  }

  /** Status label derived from catalogue publish + data upload state, or SFTP/API request status. */
  getStatusLabelForRow(row: ContributionData): string {
    const itemStatus = this.getItemStatus(row);
    if (this.isSftpOrApiItemStatus(itemStatus)) {
      const original = (row.originalData ?? {}) as Record<string, unknown>;
      const requestStatus = this.getAssetRequestStatusForRow(row);
      if (requestStatus) {
        return this.resolveSftpApiRowStatus(original, requestStatus);
      }
      return row.status ?? 'Pending';
    }

    const original = (row.originalData ?? {}) as Record<string, unknown>;
    const statusRaw =
      original['publishStatus'] ??
      original['publish_status'] ??
      original['status'] ??
      row.status ??
      '';
    return this.resolveAssetTableStatus(String(statusRaw), original);
  }

  getStatusChipClassForRow(row: ContributionData): string {
    const itemStatus = this.getItemStatus(row);
    if (this.isSftpOrApiItemStatus(itemStatus)) {
      const original = (row.originalData ?? {}) as Record<string, unknown>;
      const requestStatus = this.getAssetRequestStatusForRow(row);
      if (this.shouldUsePublishFlowForSftpApi(original, requestStatus)) {
        return this.getStatusChipClass(this.getStatusLabelForRow(row));
      }
      return this.getAssetRequestStatusChipClass(this.getStatusLabelForRow(row));
    }
    return this.getStatusChipClass(this.getStatusLabelForRow(row));
  }

  private getAssetRequestStatusChipClass(status: string): string {
    const s = String(status ?? '').trim().toLowerCase();
    if (s === 'granted' || s === 'approved') {
      return 'contribution-chip contribution-chip--status-active';
    }
    if (s === 'pending') {
      return 'contribution-chip contribution-chip--status-pending';
    }
    if (s === 'rejected') {
      return 'contribution-chip contribution-chip--status-rejected';
    }
    return this.getStatusChipClass(status);
  }

  /** CSS class for status chip in provider Manage Assets. */
  getStatusChipClass(status: string | undefined): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') {
      return 'contribution-chip contribution-chip--status-active';
    }
    if (s === 'PENDING') return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT') return 'contribution-chip contribution-chip--status-draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED' || s === 'DECLINED') {
      return 'contribution-chip contribution-chip--status-rejected';
    }
    return 'contribution-chip contribution-chip--status-default';
  }

  /** True when org admin rejected the asset publish request (DECLINED / REJECTED). */
  isAssetRejected(row: ContributionData): boolean {
    const display = (row.status ?? '').trim().toUpperCase();
    if (display === 'REJECTED') return true;
    const raw = this.getPublishStatus(row);
    return raw === 'DECLINED' || raw === 'REJECTED' || raw === 'DENIED' || raw === 'FAILED';
  }

  isOrgAdminPublishRejected(row: ContributionData): boolean {
    const original = (row.originalData ?? {}) as Record<string, unknown>;
    return this.isPublishRejectedFromItem(original);
  }

  shouldShowUploadLaterPendingInfoIcon(row: ContributionData): boolean {
    if (!this.isProviderPortal) {
      return false;
    }
    if (this.isSftpOrApiItemStatus(this.getItemStatus(row))) {
      return false;
    }
    if (String(this.getStatusLabelForRow(row)).trim().toLowerCase() !== 'pending') {
      return false;
    }
    if (!this.isPublishActiveForAsset(row)) {
      return false;
    }
    const original = (row.originalData ?? {}) as Record<string, unknown>;
    return this.getDataUploadStatusFromItem(original) !== true;
  }

  onFilterChange(filters: Record<string, any>): void {
    // Skip filter changes during initial load to prevent duplicate API calls
    if (this.isInitialLoad) {
      return;
    }

    let shouldReload = false;

    if (filters['assetType'] !== undefined) {
      const newAssetTypes = Array.isArray(filters['assetType'])
        ? filters['assetType'].map((type) => String(type ?? '').trim()).filter(Boolean)
        : [];
      if (!this.sameStringArrays(newAssetTypes, this.selectedAssetTypes)) {
        this.selectedAssetTypes = newAssetTypes;
        this.syncFilterFieldValue('assetType', [...newAssetTypes]);
        shouldReload = true;
      }
    }

    // Handle search filter separately (debounced)
    if (filters['search'] !== undefined) {
      const newSearchTerm = filters['search'] || '';
      // Only update if search term actually changed
      if (this.searchTerm !== newSearchTerm) {
        this.searchSubject.next(newSearchTerm);
        // Don't load immediately for search - wait for debounce
      }
    }

    if (shouldReload) {
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.loadContributions();
    }
  }

  onClearFilters(): void {
    this.searchTerm = '';
    this.selectedAssetTypes = [];
    this.syncFilterFieldValue('assetType', []);
    this.syncFilterFieldValue('search', '');
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadContributions();
  }

  private syncFilterFieldValue(key: string, value: unknown): void {
    const field = this.filterFields.find((f) => f.key === key);
    if (field) {
      field.value = value;
    }
  }

  private sameStringArrays(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  /** Type filter for organisation/my-assets search (dataset collection uses DataBank, models uses AiModel). */
  private buildAssetTypeSearchCriteria(): SearchCriteria[] {
    const allTypeValues = ['adex:DataBank', 'adex:AiModel', 'adex:Apps'];
    const typeValues =
      this.selectedAssetTypes.length > 0
        ? this.selectedAssetTypes.map((type) => this.assetTypeMapper.mapAssetTypeToApiFormat(type))
        : allTypeValues;

    return [
      {
        searchType: 'term',
        field: 'type',
        values: typeValues,
      },
    ];
  }

  async loadContributions(): Promise<void> {
    this.loading = true;
    try {
      const page = this.pagination.currentPage;
      const size = this.pagination.pageSize;

      const searchCriteria = this.buildAssetTypeSearchCriteria();

      const searchTrimmed = (this.searchTerm || '').trim();

      let response: any;
      if (searchTrimmed !== '' && this.isOrgAdminPortal) {
        response = await this.catalogueApiService.getOrganisationAsset(
          page,
          size,
          this.currentSortApi,
          searchCriteria,
          searchTrimmed
        );
      } else if (searchTrimmed !== '' && this.isProviderPortal) {
        response = await this.catalogueApiService.getMyAssets(
          {
            q: searchTrimmed,
            autoComplete: true,
            textSearchOptions: {
              fields: [{ name: 'name', boost: 5 }],
            },
            searchCriteria,
          },
          page,
          size,
          this.currentSortApi
        );
      } else if (this.manageAssetsPortal === 'provider') {
        response = await this.catalogueApiService.getMyAssets(
          { searchCriteria },
          page,
          size,
          this.currentSortApi
        );
      } else {
        response = await this.catalogueApiService.getOrganisationAsset(
          page,
          size,
          this.currentSortApi,
          searchCriteria
        );
      }

      // Map API response to ContributionData format
      let mappedData: ContributionData[] = [];
      let totalItems = 0;

      if (response) {
        // Handle different response structures
        if (response['result'] && Array.isArray(response['result'])) {
          mappedData = response['result'].map((item: any) => this.mapItemToContribution(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapItemToContribution(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapItemToContribution(item));
          totalItems = mappedData.length;
        }
      }

      const filtered = this.filterOutDeletedAssets(mappedData);
      if (filtered.removedCount > 0) {
        totalItems = Math.max(0, totalItems - filtered.removedCount);
      }

      await this.enrichAssetRequestStatuses(filtered.rows);

      this.contributionsData = filtered.rows;
      this.pagination = {
        ...this.pagination,
        totalItems: totalItems
      };
    } catch (error) {
      console.error('Error loading contributions:', error);
      this.contributionsData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadContributions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = { ...this.pagination, pageSize: pageSize, currentPage: 1 };
    this.loadContributions();
  }

  toggleActionDropdown(event: Event, row: ContributionData): void {
    event.stopPropagation();
    event.preventDefault();
    const rowId = row.originalData?.id || JSON.stringify(row);
    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    const btn = event.currentTarget as HTMLElement;

    const open = (): void => {
      const itemCount = this.getActionDropdownItemCount(row);
      this.openDropdownId = rowId;
      this.actionDropdownRow = row;
      this.actionDropdownOverlay.open({
        anchor: btn,
        itemCount,
        onPosition: (pos) => {
          this.overlayPosition = pos;
          this.cdr.markForCheck();
        },
        onAnchorDetached: () => this.closeActionDropdown(),
      });
    };

    const needsMediaUrlPrefetch =
      (this.isFileResourceServer(row) || this.isModelAsset(row)) &&
      !this.modelUsesMediaUrl(row);

    void this.loadAdditionalInfoForRow(row);

    if (needsMediaUrlPrefetch) {
      void this.ensureAssetMediaUrlLoaded(row).then(() => {
        open();
        this.cdr.detectChanges();
      });
      return;
    }

    open();
    void this.ensureAssetMediaUrlLoaded(row);
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
    this.overlayPosition = null;
    this.actionDropdownRow = null;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ContributionData | null {
    if (this.actionDropdownRow) {
      return this.actionDropdownRow;
    }
    if (this.openDropdownId == null) return null;
    const found = this.contributionsData.find(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return found ?? null;
  }

  /** Index in contributionsData of the row whose action dropdown is open; null when none. */
  get openDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.contributionsData.findIndex(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  isDropdownOpen(row: ContributionData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Row index for open View Files action menu (fixed-right z-index in app-table). */
  get viewFilesOpenDropdownRowIndex(): number | null {
    if (this.viewFilesOpenDropdownKey == null) return null;
    const idx = this.viewFilesTableRows.findIndex((r) => this.viewFilesRowKey(r) === this.viewFilesOpenDropdownKey);
    return idx >= 0 ? idx : null;
  }

  private viewFilesRowKey(row: DatasetFileRow): string {
    return String(row.id || row.fileName || '');
  }

  toggleViewFilesActionDropdown(event: Event, row: DatasetFileRow): void {
    event.stopPropagation();
    event.preventDefault();
    const key = this.viewFilesRowKey(row);
    if (!key) {
      return;
    }
    if (this.viewFilesOpenDropdownKey === key) {
      this.closeViewFilesActionDropdown();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    this.viewFilesOpenDropdownKey = key;
    this.viewFilesDropdownRow = row;
    this.viewFilesActionDropdownOverlay.open({
      anchor: btn,
      itemCount: 2,
      onPosition: (pos) => {
        this.viewFilesDropdownOverlayPosition = pos;
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeViewFilesActionDropdown(),
    });
  }

  closeViewFilesActionDropdown(): void {
    this.viewFilesActionDropdownOverlay.close();
    this.viewFilesOpenDropdownKey = null;
    this.viewFilesDropdownOverlayPosition = null;
    this.viewFilesDropdownRow = null;
  }

  isViewFilesActionDropdownOpen(row: DatasetFileRow): boolean {
    return this.viewFilesOpenDropdownKey === this.viewFilesRowKey(row);
  }

  onViewFilesOverlayDownload(): void {
    const row = this.viewFilesDropdownRow;
    this.closeViewFilesActionDropdown();
    if (row) {
      void this.onDownloadDatasetFile(row);
    }
  }

  onViewFilesOverlayDelete(): void {
    const row = this.viewFilesDropdownRow;
    this.closeViewFilesActionDropdown();
    if (row) {
      this.openViewFilesDeleteConfirmDialog(row);
    }
  }

  handleAction(action: string, row?: ContributionData | null): void {
    const activeRow = row ?? this.actionDropdownRow ?? this.openDropdownRow;
    if (!activeRow) {
      console.warn('Action ignored: no row context for', action);
      return;
    }
    this.closeActionDropdown();

    const original = activeRow.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];

    if (action !== 'view' && action !== 'view-feedback' && this.isAssetRejected(activeRow)) {
      return;
    }
    if (action === 'view-feedback' && !this.canViewFeedback(activeRow)) {
      return;
    }

    if (action === 'ogc-upload-modal' && this.isOgcFeatureDataUploadComplete(activeRow)) {
      this.toasterService.error('This cannot be done and is a one-time process.');
      return;
    }

    if (
      (action === 'ogc-upload-modal' ||
        action === 'stac-edit' ||
        action === 'upload-more') &&
      (action === 'upload-more'
        ? this.isUploadMoreFilesDisabled(activeRow)
        : this.isOgcUploadActionDisabled(activeRow))
    ) {
      this.toasterService.error('Upload is unavailable while publish status is Pending.');
      return;
    }

    if (action === 'view') {
      this.viewAsset(activeRow);
    } else if (action === 'view-feedback') {
      void this.openViewFeedbackModal(activeRow);
    } else if (action === 'modify') {
      if (this.isModifyAssetDisabled(activeRow)) {
        this.toasterService.error(
          this.getModifyAssetDisabledTooltip(activeRow) ??
            'Modify is only available while publish status is Pending',
        );
        return;
      }
      void this.openModifyModal(activeRow);
    } else if (action === 'view-files') {
      this.openFileUploadModal(activeRow);
    } else if (action === 'ngsild-upload-modal') {
      this.openNgsildUploadModal(activeRow);
    } else if (action === 'ogc-upload-modal') {
      void this.openOgcUploadModal(activeRow);
    } else if (action === 'stac-edit') {
      void this.openStacOnboardingModal(activeRow);
    } else if (action === 'upload-more') {
      if (this.isProviderPortal && this.supportsConnectionDetailsResourceServer(activeRow)) {
        void this.openNgsildUploadModal(activeRow);
      } else {
        this.navigateToUploadMore(activeRow);
      }
    } else if (action === 'additional-info') {
      void this.openAdditionalInfoModal(activeRow);
    } else if (action === 'delete') {
      if (!assetId) {
        console.warn('Unable to remove asset: missing asset id', activeRow);
        return;
      }
      this.closeActionDropdown();
      void this.removeAsset(String(assetId));
    }
  }

  /** Remove asset from catalogue and update the table without a full page refresh. */
  private async removeAsset(assetId: string): Promise<void> {
    const id = String(assetId).trim();
    if (!id) return;

    try {
      await this.catalogueApiService.deleteItem(id);
      this.toasterService.success("You've successfully removed the asset");
      this.removeDeletedAssetFromTable(id);

      if (this.contributionsData.length === 0 && this.pagination.currentPage > 1) {
        this.pagination = {
          ...this.pagination,
          currentPage: this.pagination.currentPage - 1,
        };
      }

      await this.loadContributions();
    } catch (err) {
      console.error('Error removing asset:', err);
      this.toasterService.error('Could not remove the asset. Please try again.');
    }
  }

  private removeDeletedAssetFromTable(assetId: string): void {
    const id = String(assetId).trim();
    if (!id) return;

    this.deletedAssetIds.add(id);
    this.contributionsData = this.contributionsData.filter(
      (row) => this.getRowAssetId(row) !== id
    );
    this.pagination = {
      ...this.pagination,
      totalItems: Math.max(0, this.pagination.totalItems - 1),
    };
    this.closeActionDropdown();
    this.cdr.detectChanges();
  }

  private filterOutDeletedAssets(rows: ContributionData[]): {
    rows: ContributionData[];
    removedCount: number;
  } {
    if (this.deletedAssetIds.size === 0) {
      return { rows, removedCount: 0 };
    }
    const before = rows.length;
    const filtered = rows.filter((row) => {
      const id = this.getRowAssetId(row);
      return !id || !this.deletedAssetIds.has(id);
    });
    return { rows: filtered, removedCount: before - filtered.length };
  }

  /** Catalogue `itemStatus` for the row (e.g. SFTP, API, FILE). */
  private getItemStatus(row: ContributionData): string {
    const original = row.originalData || {};
    const status =
      original.itemStatus ??
      original.item_status ??
      original['itemStatus'] ??
      '';
    return String(status).trim().toUpperCase();
  }

  /** True when the catalogue row is a model (AiModel). */
  isModelAsset(row: ContributionData): boolean {
    const typeLabel = (row.assetType || '').toLowerCase();
    if (typeLabel === 'model') return true;
    const raw = row.originalData?.type || row.originalData?.assetType || row.originalData?.asset_type || '';
    const rawStr = (Array.isArray(raw) ? raw[0] : raw || '').toString().toLowerCase();
    return rawStr.includes('aimodel') || rawStr.includes('model');
  }

  /** Datasets always show RS icons; models show icon only for file-server rows. */
  shouldShowResourceServerIcon(row: ContributionData): boolean {
    return !this.isModelAsset(row) || this.isFileResourceServer(row);
  }

  /** Model linked to an external Data Source URL (non-placeholder `mediaURL`). */
  modelUsesMediaUrl(row: ContributionData): boolean {
    return !!this.getAssetMediaUrlFromRow(row);
  }

  /** Display label for View Files / Connection Details modals. */
  getManageAssetsAssetKindLabel(row: ContributionData | null): string {
    if (!row) return 'Asset';
    return this.isModelAsset(row) ? 'Model' : 'Dataset';
  }

  hasAdditionalInfo(row: ContributionData | null | undefined): boolean {
    const id = this.getRowAssetId(row);
    if (!id) return false;
    return hasFaqOrInfoFeedback(this.additionalInfoCache.get(id));
  }

  getAdditionalInfoActionLabel(row: ContributionData): string {
    return this.hasAdditionalInfo(row)
      ? 'Modify Additional Info'
      : 'Add Additional Info';
  }

  getAdditionalInfoModalAssetId(): string {
    return this.getRowAssetId(this.additionalInfoModalRow) ?? '';
  }

  getAdditionalInfoModalPreloaded(): ProviderFeedbackItem[] | null {
    const id = this.getRowAssetId(this.additionalInfoModalRow);
    if (!id || !this.additionalInfoCache.has(id)) {
      return null;
    }
    return this.additionalInfoCache.get(id) ?? [];
  }

  async openAdditionalInfoModal(row: ContributionData): Promise<void> {
    const id = this.getRowAssetId(row);
    if (!id) {
      this.toasterService.error('Unable to open additional information: missing asset ID');
      return;
    }
    if (!this.additionalInfoCache.has(id)) {
      await this.fetchAdditionalInfo(id);
    }
    this.additionalInfoModalRow = row;
    this.showAdditionalInfoModal = true;
    this.cdr.detectChanges();
  }

  closeAdditionalInfoModal(): void {
    this.showAdditionalInfoModal = false;
    this.additionalInfoModalRow = null;
  }

  onAdditionalInfoSaved(items: ProviderFeedbackItem[]): void {
    const id = this.getRowAssetId(this.additionalInfoModalRow);
    if (id) {
      this.additionalInfoCache.set(id, items);
    }
    this.closeAdditionalInfoModal();
  }

  /** GET `/iudx/v2/provider/feedback` for the row whose Action menu just opened. */
  private loadAdditionalInfoForRow(row: ContributionData): Promise<void> {
    const id = this.getRowAssetId(row);
    if (!id) {
      return Promise.resolve();
    }
    return this.fetchAdditionalInfo(id).then(() => undefined);
  }

  private fetchAdditionalInfo(id: string): Promise<ProviderFeedbackItem[]> {
    const pending = this.additionalInfoInflight.get(id);
    if (pending) {
      return pending;
    }
    const next = this.catalogueApiService
      .getProviderFeedback(id)
      .then((items) => {
        this.additionalInfoCache.set(id, items);
        this.cdr.detectChanges();
        return items;
      })
      .finally(() => {
        this.additionalInfoInflight.delete(id);
      });
    this.additionalInfoInflight.set(id, next);
    return next;
  }

  /**
   * View Files: file-server datasets/models with files already uploaded (no Data Source URL).
   * When files are not uploaded yet, show Upload Files instead.
   */
  canViewFiles(row: ContributionData): boolean {
    if (!this.canUploadMoreFiles(row)) return false;
    if (this.modelUsesMediaUrl(row)) return false;
    const isFileFlow = this.isModelAsset(row) || this.isFileResourceServer(row);
    if (!isFileFlow) return false;
    return this.getDataUploadStatusFromItem(row.originalData) === true;
  }

  /** Upload-more menu for non–file-server datasets (SFTP/API labels or navigate to uploads). */
  canShowUploadMoreAction(row: ContributionData): boolean {
    if (this.isModelAsset(row)) return false;
    return (
      this.canUploadMoreFiles(row) &&
      !this.isFileResourceServer(row) &&
      !this.isOgcResourceServer(row) &&
      !this.canShowOnboardingStyleUploadModalAction(row)
    );
  }

  /** OGC Feature datasets: GeoPackage upload modal. */
  canShowOgcFeatureUploadAction(row: ContributionData): boolean {
    return (
      this.canUploadMoreFiles(row) &&
      this.isOgcResourceServer(row) &&
      this.getOgcOnboardingModeFromRow(row) === 'feature'
    );
  }

  /** OGC Raster datasets: unified Edit STAC flow (collection, item, and asset steps). */
  canShowOgcRasterEditStacAction(row: ContributionData): boolean {
    return (
      this.canUploadMoreFiles(row) &&
      this.isOgcResourceServer(row) &&
      this.getOgcOnboardingModeFromRow(row) === 'raster'
    );
  }

  isOgcResourceServer(row: ContributionData): boolean {
    return this.getNormalizedResourceServerIdFromRow(row) === 'ogc';
  }

  /** Detect vector vs raster OGC mode by inspecting `queryTypes` in the catalogue resourceServer entry. */
  getOgcOnboardingModeFromRow(row: ContributionData): 'feature' | 'raster' | null {
    if (!this.isOgcResourceServer(row)) return null;
    const original = row.originalData || {};
    const rs = original.resourceServer ?? original.resource_server;
    const rsArray: unknown[] = Array.isArray(rs) ? rs : rs ? [rs] : [];
    for (const entry of rsArray) {
      if (entry && typeof entry === 'object') {
        const qt = (entry as Record<string, unknown>)['queryTypes'];
        if (Array.isArray(qt)) {
          if (qt.includes('FEATURE')) return 'feature';
          if (qt.includes('STAC')) return 'raster';
        }
      }
    }
    return null;
  }

  /** Display label for the Resource Server column. OGC rows show "OGC Vector" or "OGC Raster" based on queryTypes. */
  getResourceServerDisplayLabel(row: ContributionData): string {
    if (this.isOgcResourceServer(row)) {
      const mode = this.getOgcOnboardingModeFromRow(row);
      if (mode === 'feature') return 'OGC Vector';
      if (mode === 'raster') return 'OGC Raster';
    }
    return row.resourceServer || '—';
  }

  /**
   * Connection Details modal:
   * - Models / file-server datasets: Upload Later → File Upload or Data Source URL;
   *   once a Data Source URL is set the modal locks to read-only URL display.
   *   Once files are uploaded (`dataUploadStatus`), hide this action — use View Files instead.
   * - Gateway: always show when any connection option is set (Data Source URL, SFTP, API, Direct)
   *   or when still pending configuration (add flow).
   * - NGSI-LD: always when not yet configured; once configured, org admin requires ACTIVE publish;
   *   provider allows pending and active.
   * OGC uses the dedicated Upload Files modal instead.
   */
  canShowOnboardingStyleUploadModalAction(row: ContributionData): boolean {
    if (!this.canUploadMoreFiles(row)) {
      return false;
    }
    if (this.isOgcResourceServer(row)) {
      return false;
    }
    if (this.isModelAsset(row) || this.isFileResourceServer(row)) {
      // Data Source URL: keep Connection Details (read-only view).
      if (this.modelUsesMediaUrl(row)) {
        return true;
      }
      // Files already uploaded — View Files covers this; hide Connection Details.
      if (this.getDataUploadStatusFromItem(row.originalData) === true) {
        return false;
      }
      // Upload Later / no files yet: File Upload + Data Source URL.
      return true;
    }
    if (!this.supportsConnectionDetailsResourceServer(row)) {
      return false;
    }
    // Upload-later / pending assets with no SFTP, API, Direct, or mediaURL yet.
    if (!this.hasConnectionDetailsConfigured(row)) {
      return true;
    }
    // Gateway: Data Source URL or any upload option → always show Connection Details.
    if (this.getNormalizedResourceServerIdFromRow(row) === 'gateway') {
      return true;
    }
    if (this.isProviderPortal) {
      return true;
    }
    return this.isPublishActiveForAsset(row);
  }

  /** True when mediaURL, SFTP/API, or Direct connection details are already on the asset. */
  private hasConnectionDetailsConfigured(row: ContributionData): boolean {
    if (this.modelUsesMediaUrl(row)) {
      return true;
    }
    const status = this.getItemStatus(row);
    return this.isSftpOrApiItemStatus(status) || this.isDirectItemStatus(status);
  }

  /** Resource servers that use the Connection Details (`lib-ngsild-upload-files`) modal. */
  supportsConnectionDetailsResourceServer(row: ContributionData): boolean {
    const id = this.getNormalizedResourceServerIdFromRow(row);
    return id === 'ngsi-ld' || id === 'gateway';
  }

  /**
   * Menu label for the onboarding-style upload / connection modal.
   * File-server Upload Later → "Upload Files"; Data Source URL / NGSI-LD / Gateway → "Connection Details".
   */
  getOnboardingStyleUploadActionLabel(row: ContributionData): string {
    if (
      (this.isModelAsset(row) || this.isFileResourceServer(row)) &&
      !this.modelUsesMediaUrl(row)
    ) {
      return 'Upload Files';
    }
    return 'Connection Details';
  }

  /** View Feedback is only for assets whose publish status is approved (shown as Active). */
  canViewFeedback(row: ContributionData): boolean {
    const status = String(this.getStatusLabelForRow(row)).trim().toLowerCase();
    return status === 'active' || status === 'approved' || status === 'granted';
  }

  private getActionDropdownItemCount(row: ContributionData): number {
    if (this.isAssetRejected(row)) {
      return this.canViewFeedback(row) ? 2 : 1;
    }
    let count = 3;
    if (
      this.canViewFiles(row) ||
      this.canShowUploadMoreAction(row) ||
      this.canShowOnboardingStyleUploadModalAction(row) ||
      this.canShowOgcFeatureUploadAction(row) ||
      this.canShowOgcRasterEditStacAction(row)
    ) {
      count += 1;
    }
    return count;
  }

  /** Catalogue publish status (e.g. ACTIVE when live in catalogue). */
  private getPublishStatus(row: ContributionData): string {
    const original = row.originalData || {};
    const s =
      original.publishStatus ??
      original.publish_status ??
      original.status ??
      '';
    return String(s).trim().toUpperCase();
  }

  isPublishActiveForAsset(row: ContributionData): boolean {
    const s = this.getPublishStatus(row);
    return s === 'ACTIVE' || s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE';
  }

  isPublishPendingForAsset(row: ContributionData): boolean {
    return this.getPublishStatus(row) === 'PENDING';
  }

  /** Upload More Files is disabled while the asset is awaiting publish approval. */
  isUploadMoreFilesDisabled(row: ContributionData): boolean {
    return this.isPublishPendingForAsset(row);
  }

  /** Modify Asset is only available while the table status is Pending. */
  isModifyAssetDisabled(row: ContributionData): boolean {
    return String(this.getStatusLabelForRow(row)).trim().toLowerCase() !== 'pending';
  }

  getModifyAssetDisabledTooltip(row: ContributionData): string | null {
    return this.isModifyAssetDisabled(row)
      ? 'Modify is only available while publish status is Pending'
      : null;
  }

  /**
   * OGC Feature GeoPackage upload is a one-time process once `dataUploadStatus` is true.
   * OGC Raster edit actions remain enabled for Pending datasets because upload/onboarding
   * completion is part of the contribution workflow.
   */
  isOgcUploadActionDisabled(row: ContributionData): boolean {
    if (this.getOgcOnboardingModeFromRow(row) === 'feature') {
      return this.isOgcFeatureDataUploadComplete(row);
    }
    if (this.getOgcOnboardingModeFromRow(row) === 'raster') {
      return false;
    }
    return this.isUploadMoreFilesDisabled(row);
  }

  /** True when OGC Feature dataset has already completed its one-time file upload. */
  isOgcFeatureDataUploadComplete(row: ContributionData): boolean {
    return (
      this.getOgcOnboardingModeFromRow(row) === 'feature' &&
      this.getDataUploadStatusFromItem(row.originalData as Record<string, unknown>) === true
    );
  }

  getOgcUploadActionDisabledTooltip(row: ContributionData): string | null {
    if (this.isOgcFeatureDataUploadComplete(row)) {
      return 'This cannot be done and is a one-time process.';
    }
    return this.isOgcUploadActionDisabled(row)
      ? 'Unavailable while publish status is Pending'
      : null;
  }

  /** Normalized resource server id from row (table label or `originalData.resourceServer`). */
  getNormalizedResourceServerIdFromRow(row: ContributionData): ResourceServerId {
    const original = row.originalData || {};
    const rs = original.resourceServer ?? original.resource_server;
    if (Array.isArray(rs) && rs.length > 0) {
      const first = rs[0];
      if (first && typeof first === 'object' && !Array.isArray(first)) {
        const token =
          (first as Record<string, unknown>)['type'] ?? (first as Record<string, unknown>)['name'];
        return normalizeResourceServerId(String(token ?? ''));
      }
    }
    if (rs && typeof rs === 'object' && !Array.isArray(rs)) {
      const token =
        (rs as Record<string, unknown>)['type'] ??
        (rs as Record<string, unknown>)['name'];
      return normalizeResourceServerId(String(token ?? ''));
    }
    return normalizeResourceServerId(row.resourceServer);
  }

  isNgsiLdOrGatewayResourceServer(row: ContributionData): boolean {
    const id = this.getNormalizedResourceServerIdFromRow(row);
    return id === 'ngsi-ld' || id === 'gateway';
  }

  /** True when catalogue `resourceServer` is File / file-server. */
  isFileResourceServer(row: ContributionData): boolean {
    const label = (row.resourceServer ?? '').trim().toUpperCase();
    if (label === 'FILE' || label.includes('FILE SERVER')) {
      return true;
    }

    const original = row.originalData || {};
    const rs = original.resourceServer ?? original.resource_server;
    if (Array.isArray(rs) && rs.length > 0) {
      const first = rs[0];
      if (first && typeof first === 'object' && !Array.isArray(first)) {
        const token =
          (first as Record<string, unknown>)['type'] ??
          (first as Record<string, unknown>)['name'];
        return normalizeResourceServerId(String(token ?? '')) === 'file-server';
      }
    }
    if (rs && typeof rs === 'object' && !Array.isArray(rs)) {
      const token =
        (rs as Record<string, unknown>)['type'] ??
        (rs as Record<string, unknown>)['name'];
      return normalizeResourceServerId(String(token ?? '')) === 'file-server';
    }
    return false;
  }

  /** Action menu label for upload / data-source row based on `itemStatus`. */
  getUploadMoreFilesActionLabel(row: ContributionData): string {
    const method = uploadMethodFromItemStatus(this.getItemStatus(row));
    if (method === 'SFTP') return 'SFTP Details';
    if (method === 'API') return 'API Details';
    return 'Upload More Files';
  }

  /** Dataset & model rows can upload additional files via the contribution upload step. */
  canUploadMoreFiles(row: ContributionData): boolean {
    const typeLabel = (row.assetType || '').toLowerCase();
    if (typeLabel === 'dataset' || typeLabel === 'model') return true;
    const raw = row.originalData?.type || row.originalData?.assetType || row.originalData?.asset_type || '';
    const rawStr = (Array.isArray(raw) ? raw[0] : raw || '').toString().toLowerCase();
    return (
      rawStr.includes('databank') ||
      rawStr.includes('database') ||
      rawStr.includes('aimodel') ||
      rawStr.includes('model')
    );
  }

  async openNgsildUploadModal(row: ContributionData): Promise<void> {
    const original = row.originalData || {};
    const assetId =
      original.id || original['assetId'] || original['datasetId'] || original['modelId'];
    if (!assetId) {
      this.toasterService.error('Unable to open upload details: missing asset ID');
      return;
    }
    const idStr = String(assetId).trim();
    let modalRow = row;

    try {
      const assetData = await this.fetchCatalogueItemById(idStr);
      if (assetData) {
        modalRow = this.mapItemToContribution(assetData);
        const mediaUrl = this.extractMediaUrlFromApiItem(assetData);
        if (mediaUrl) {
          this.cacheAssetMediaUrl(idStr, mediaUrl, modalRow);
        }
      }
    } catch (err: unknown) {
      console.warn('Could not refresh asset for connection details:', err);
    }

    /** Same as `GET …/iudx/v2/auth/asset/request?page=1&size=10&type=…&assetId=…` — fire on menu click so SFTP/API Details always hits auth. */
    const status = this.getItemStatus(modalRow);
    if (isSftpOrApiItemStatus(status) && !this.getAssetMediaUrlFromRow(modalRow)) {
      void this.authApiService
        .getAssetRequests(1, 10, status, undefined, idStr)
        .catch((err: unknown) => {
          console.warn('asset/request prefetch (Manage Assets details):', err);
        });
    }

    this.ngsildUploadModalRow = modalRow;
    this.showNgsildUploadModal = true;
    this.cdr.detectChanges();
  }

  closeNgsildUploadModal(): void {
    this.showNgsildUploadModal = false;
    this.ngsildUploadModalRow = null;
    this.cdr.detectChanges();
  }

  async openOgcUploadModal(row: ContributionData): Promise<void> {
    const original = row.originalData || {};
    const assetId =
      original.id || original['assetId'] || original['datasetId'] || original['modelId'];
    if (!assetId) {
      this.toasterService.error('Unable to open upload: missing asset ID');
      return;
    }
    const idStr = String(assetId).trim();
    let modalRow = row;

    try {
      const assetData = await this.fetchCatalogueItemById(idStr);
      if (assetData) {
        modalRow = this.mapItemToContribution(assetData);
      }
    } catch (err: unknown) {
      console.warn('Could not refresh asset for OGC upload:', err);
    }

    if (this.isOgcFeatureDataUploadComplete(modalRow)) {
      this.toasterService.error('This cannot be done and is a one-time process.');
      return;
    }

    this.ogcUploadModalRow = modalRow;
    this.ogcUploadLoadKey += 1;
    this.showOgcUploadModal = true;
    this.cdr.detectChanges();
  }

  closeOgcUploadModal(): void {
    this.showOgcUploadModal = false;
    this.ogcUploadModalRow = null;
    this.cdr.detectChanges();
  }

  async openStacOnboardingModal(row: ContributionData): Promise<void> {
    const original = row.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];
    if (!assetId) {
      this.toasterService.error('Unable to open STAC editor: missing asset ID');
      return;
    }
    let modalRow = row;
    try {
      const assetData = await this.fetchCatalogueItemById(String(assetId).trim());
      if (assetData) {
        modalRow = this.mapItemToContribution(assetData);
      }
    } catch (err: unknown) {
      console.warn('Could not refresh asset for STAC editor:', err);
    }
    this.stacOnboardingModalRow = modalRow;
    this.showStacOnboardingModal = true;
    this.cdr.detectChanges();
  }

  closeStacOnboardingModal(): void {
    this.showStacOnboardingModal = false;
    this.stacOnboardingModalRow = null;
    this.stacOnboardingCollectionExists = false;
    this.cdr.detectChanges();
  }

  getStacOnboardingModalItemId(): string | null {
    const original = this.stacOnboardingModalRow?.originalData || {};
    const id = original.id ?? original['assetId'] ?? original['datasetId'] ?? original['modelId'];
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  getStacOnboardingModalDatasetTitle(): string {
    const api = this.stacOnboardingModalRow?.originalData || {};
    return (
      (api.name ?? api.label ?? api.title ?? this.stacOnboardingModalRow?.assetName ?? '')
        .toString()
        .trim() || ''
    );
  }

  getStacOnboardingModalDatasetDescription(): string {
    const api = this.stacOnboardingModalRow?.originalData || {};
    return (
      (api.shortDescription ?? api.short_description ?? api.description ?? '').toString().trim() ||
      ''
    );
  }

  onStacExistingCollectionDetected(exists: boolean): void {
    this.stacOnboardingCollectionExists = exists;
    this.cdr.detectChanges();
  }

  onStacOnboardingModalSuccess(message: string): void {
    this.toasterService.success(message);
    if (/assets uploaded successfully/i.test(message)) {
      this.closeStacOnboardingModal();
      void this.loadContributions();
    }
  }

  onStacOnboardingModalError(message: string): void {
    this.toasterService.error(message);
  }

  onStacOnboardingModalWarning(message: string): void {
    this.toasterService.warning(message);
  }

  getOgcUploadModalItemId(): string | null {
    const original = this.ogcUploadModalRow?.originalData || {};
    const id =
      original.id ?? original['assetId'] ?? original['datasetId'] ?? original['modelId'];
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  getOgcUploadModalDatasetTitle(): string {
    const api = this.ogcUploadModalRow?.originalData || {};
    return (
      (api.name ?? api.label ?? api.title ?? this.ogcUploadModalRow?.assetName ?? '')
        .toString()
        .trim() || ''
    );
  }

  getOgcUploadModalDatasetDescription(): string {
    const api = this.ogcUploadModalRow?.originalData || {};
    return (
      (api.shortDescription ?? api.short_description ?? api.description ?? '')
        .toString()
        .trim() || ''
    );
  }

  onOgcUploadModalSuccess(): void {
    this.toasterService.success('GeoPackage uploaded successfully');
    this.closeOgcUploadModal();
    void this.loadContributions();
  }

  onOgcUploadModalError(message: string): void {
    this.toasterService.error(message);
  }

  getNgsildUploadModalItemId(): string | null {
    const original = this.ngsildUploadModalRow?.originalData || {};
    const id =
      original.id ?? original['assetId'] ?? original['datasetId'] ?? original['modelId'];
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  getNgsildUploadModalResourceServer(): ResourceServerId {
    if (!this.ngsildUploadModalRow) return 'ngsi-ld';
    if (this.isModelAsset(this.ngsildUploadModalRow)) {
      return 'file-server';
    }
    return this.getNormalizedResourceServerIdFromRow(this.ngsildUploadModalRow);
  }

  getNgsildUploadModalConnectionDetailsLedeKind(): ConnectionDetailsModalLedeKind {
    const row = this.ngsildUploadModalRow;
    const directPushOnly =
      row != null &&
      !this.getNgsildUploadModalMediaUrl() &&
      this.isDirectItemStatus(this.getItemStatus(row));
    return getConnectionDetailsModalLedeKind(
      this.getNgsildUploadModalResourceServer(),
      {
        dataSourceReadOnly: this.getNgsildUploadModalLockedModalMode() === 'data-source',
        directPushOnly,
      },
    );
  }

  getNgsildUploadModalDataSourceUrlEnabled(): boolean {
    const server = this.getNgsildUploadModalResourceServer();
    // NGSI-LD / Gateway: always show Data Source URL with SFTP/API on Connection Details.
    if (server === 'ngsi-ld' || server === 'gateway') {
      return true;
    }
    const permission =
      this.getNgsildOnboardingPayloadForUploadModal()?.basic?.permission;
    if (!permission) {
      return true;
    }
    const p = String(permission).trim().toUpperCase();
    return p === 'OPEN' || p === 'PUBLIC';
  }

  getNgsildUploadModalFileFormat(): string {
    const original = this.ngsildUploadModalRow?.originalData || {};
    const fmt = original.fileFormat ?? original.file_format ?? 'csv';
    return String(fmt).trim() || 'csv';
  }

  private extractMediaUrlFromApiItem(
    item: Record<string, unknown> | null | undefined,
  ): string {
    if (!item) return '';
    const direct =
      item['mediaURL'] ??
      item['media_url'] ??
      item['mediaUrl'] ??
      item['sampleDataUrl'] ??
      item['sample_data_url'];
    if (direct != null && String(direct).trim()) {
      const url = String(direct).trim();
      return this.normalizeMediaUrlForDisplay(url);
    }
    return '';
  }

  private async fetchCatalogueItemById(
    assetId: string,
  ): Promise<Record<string, unknown> | null> {
    const response = await this.catalogueApiService.getItemById(assetId);
    if (response?.result) {
      return Array.isArray(response.result) ? response.result[0] : response.result;
    }
    if (response?.data) {
      return Array.isArray(response.data) ? response.data[0] : response.data;
    }
    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }
    return response && typeof response === 'object' ? (response as Record<string, unknown>) : null;
  }

  /** Persist `mediaURL` for action-menu labels and connection-details modal. */
  private cacheAssetMediaUrl(
    assetId: string,
    mediaUrl: string,
    rowHint?: ContributionData | null,
  ): void {
    const id = assetId.trim();
    const url = mediaUrl.trim();
    if (!id || !url) return;

    this.assetMediaUrlByAssetId.set(id, url);

    const mergeIntoRow = (row: ContributionData): ContributionData => ({
      ...row,
      originalData: { ...(row.originalData || {}), mediaURL: url },
    });

    if (rowHint && this.getRowAssetId(rowHint) === id) {
      const merged = mergeIntoRow(rowHint);
      if (this.actionDropdownRow && this.getRowAssetId(this.actionDropdownRow) === id) {
        this.actionDropdownRow = merged;
      }
      if (this.ngsildUploadModalRow && this.getRowAssetId(this.ngsildUploadModalRow) === id) {
        this.ngsildUploadModalRow = merged;
      }
    }

    const idx = this.contributionsData.findIndex((r) => this.getRowAssetId(r) === id);
    if (idx >= 0) {
      this.contributionsData[idx] = mergeIntoRow(this.contributionsData[idx]);
    }
  }

  /**
   * List/search APIs often omit `mediaURL`; load it when the action menu opens so the
   * Connection Details modal can open in data-source view when applicable.
   */
  private async ensureAssetMediaUrlLoaded(row: ContributionData): Promise<void> {
    const needsMediaUrl =
      this.isModelAsset(row) ||
      this.isFileResourceServer(row) ||
      this.supportsConnectionDetailsResourceServer(row);
    if (!needsMediaUrl) {
      return;
    }
    const assetId = this.getRowAssetId(row);
    if (!assetId) {
      return;
    }
    if (this.getAssetMediaUrlFromRow(row)) {
      return;
    }
    if (this.assetMediaUrlFetchInFlight.has(assetId)) {
      return;
    }

    this.assetMediaUrlFetchInFlight.add(assetId);
    try {
      const assetData = await this.fetchCatalogueItemById(assetId);
      const mediaUrl = this.extractMediaUrlFromApiItem(assetData);
      if (mediaUrl) {
        this.cacheAssetMediaUrl(assetId, mediaUrl, row);
        this.cdr.detectChanges();
      }
    } catch (err: unknown) {
      console.warn('Could not load mediaURL for action menu:', err);
    } finally {
      this.assetMediaUrlFetchInFlight.delete(assetId);
    }
  }

  /** Catalogue `mediaURL` when the asset uses an external data source. */
  getAssetMediaUrlFromRow(row: ContributionData | null): string {
    if (!row) return '';
    const assetId = this.getRowAssetId(row);
    const cached = assetId ? this.assetMediaUrlByAssetId.get(assetId) : undefined;
    if (cached?.trim()) {
      return cached.trim();
    }
    return this.extractMediaUrlFromApiItem(row.originalData as Record<string, unknown>);
  }

  getNgsildUploadModalMediaUrl(): string | null {
    const url = this.getAssetMediaUrlFromRow(this.ngsildUploadModalRow);
    return url || null;
  }

  /** When `mediaURL` exists, only show the Data Source URL flow (no file/SFTP/API upload). */
  getNgsildUploadModalLockedModalMode(): 'data-source' | null {
    return this.getNgsildUploadModalMediaUrl() ? 'data-source' : null;
  }

  /** After SFTP/API was saved, constrain the onboarding modal to that method while loading submitted details. */
  getNgsildUploadModalLockedMethod(): string | null {
    if (this.getNgsildUploadModalMediaUrl()) {
      return null;
    }
    const row = this.ngsildUploadModalRow;
    if (!row) return null;
    const status = this.getItemStatus(row);
    const method = uploadMethodFromItemStatus(status);
    if (method === 'SFTP') return 'sftp-upload';
    if (method === 'API') return 'api-integration';
    if (status === 'DIRECT') return 'direct-push';
    return null;
  }

  /**
   * Manage Assets SFTP/API details: fetch `/auth/asset/request` with matching `type`
   * and `assetId` so previously submitted SFTP/API config can be prefilled read-only.
   * Uses compound types (`VERIFIED-SFTP`, etc.) when present on the asset.
   */
  getNgsildExistingAssetRequestType(): VerifiedUploadRequestType | NgsildUploadRequestType | null {
    if (this.getNgsildUploadModalMediaUrl()) {
      return null;
    }
    const row = this.ngsildUploadModalRow;
    if (!row) return null;
    const status = this.getItemStatus(row);
    const method = uploadMethodFromItemStatus(status);
    if (!method) return null;
    if (status.includes('-')) {
      return status as VerifiedUploadRequestType;
    }
    // Legacy bare SFTP/API itemStatus — still query as SFTP|API
    return method;
  }

  /** Same catalogue-backed payload onboarding passes to Add Data modal (SFTP / API / data source URL). */
  getNgsildOnboardingPayloadForUploadModal(): NgsildOnboardingPayload | null {
    return this.buildNgsildOnboardingPayloadFromContribution(this.ngsildUploadModalRow);
  }

  /** Map organisation asset row → wizard payload shape for `lib-ngsild-upload-files`. */
  private buildNgsildOnboardingPayloadFromContribution(
    row: ContributionData | null,
  ): NgsildOnboardingPayload | null {
    if (!row) return null;
    const api = row.originalData || {};
    const typeLabel = (row.assetType || '').toLowerCase();
    const rawType = api.type ?? api.assetType ?? api.asset_type;
    const rawTypeStr = (
      Array.isArray(rawType) ? (rawType[0] ?? '') : rawType ?? ''
    )
      .toString()
      .toLowerCase();
    const isDatasetLike =
      typeLabel === 'dataset' ||
      rawTypeStr.includes('databank') ||
      rawTypeStr.includes('database');
    const isModelLike =
      typeLabel === 'model' ||
      rawTypeStr.includes('aimodel') ||
      rawTypeStr.includes('model');
    if (!isDatasetLike && !isModelLike) return null;

    const title = (
      api.name ??
      api.label ??
      api.title ??
      row.assetName ??
      ''
    )
      .toString()
      .trim();
    if (!title) return null;

    const accessPolicy = (api.accessPolicy ?? api.access_policy ?? 'OPEN')
      .toString()
      .toUpperCase();
    const permission: 'Open' | 'Restricted' =
      accessPolicy === 'RESTRICTED' || accessPolicy === 'PRIVATE'
        ? 'Restricted'
        : 'Open';

    let yearRange: NgsildYearRange | null = null;
    const yr = api.yearRange ?? api.year_range;
    if (typeof yr === 'string' && yr.includes('-')) {
      const parts = yr.split('-').map((p: string) => parseInt(p.trim(), 10));
      if (
        parts.length >= 2 &&
        !Number.isNaN(parts[0]) &&
        !Number.isNaN(parts[1])
      ) {
        yearRange = { startYear: parts[0], endYear: parts[1] };
      }
    } else if (yr && typeof yr === 'object' && !Array.isArray(yr)) {
      const o = yr as Record<string, unknown>;
      const sy = o['startYear'] ?? o['start_year'];
      const ey = o['endYear'] ?? o['end_year'];
      yearRange = {
        startYear: sy != null ? Number(sy) : null,
        endYear: ey != null ? Number(ey) : null,
      };
    }

    const shortDescription = (
      api.shortDescription ??
      api.short_description ??
      ''
    ).toString();
    const longDescription = (
      api.description ??
      api.longDescription ??
      ''
    ).toString();

    return {
      basic: {
        title,
        permission,
        shortDescription: shortDescription.trim() || '—',
        longDescription: longDescription.trim() || '—',
      },
      metadata: {
        tags: Array.isArray(api.tags)
          ? api.tags
          : api.tags
            ? [api.tags]
            : [],
        yearRange,
        geoCoverage: (api.geoCoverage ?? api.geo_coverage ?? '').toString(),
        uploadFrequency: (
          api.uploadFrequency ??
          api.upload_frequency ??
          'OneTime'
        ).toString(),
        license: (api.license ?? 'Open Government License, India').toString(),
        otherLicense: (api.otherLicense ?? api.other_license ?? '').toString(),
        datasetType: isModelLike
          ? (api.modelType ?? api.model_type ?? api.datasetType ?? 'Structured').toString()
          : (api.dataBankType ?? api.datasetType ?? 'Structured').toString(),
        fileFormat: (api.fileFormat ?? api.file_format ?? 'csv').toString(),
        dataDescriptors: [],
        dataSample: api.dataSample ?? api.data_sample ?? null,
      },
    };
  }

  getNgsildUploadRequestSuccessTypeLabel(): string {
    const t = this.ngsildUploadRequestSuccessType;
    if (t === 'SFTP') return 'SFTP request';
    if (t === 'API') return 'API integration';
    return 'request';
  }

  /**
   * SFTP/API success modal title. Providers need org admin + platform admin;
   * org admins only need platform admin.
   */
  getNgsildUploadRequestSuccessTitle(): string {
    return this.isProviderPortal
      ? 'Request sent for Approval'
      : 'Request sent to Platform Admin';
  }

  /**
   * SFTP/API success modal body. Providers await both org admin and platform admin.
   */
  getNgsildUploadRequestSuccessMessage(): string {
    const typeLabel = this.getNgsildUploadRequestSuccessTypeLabel();
    if (this.isProviderPortal) {
      return (
        `Your ${typeLabel} has been successfully submitted to the Platform Administrator. ` +
        `Your request is awaiting Organisation Admin approval for metadata and Platform Admin approval.`
      );
    }
    return (
      `Your ${typeLabel} has been successfully submitted to the Platform Administrator. ` +
      `Your request is awaiting approval from the Platform Admin.`
    );
  }

  getNgsildUploadRequestSuccessNextSteps(): string {
    const t = this.ngsildUploadRequestSuccessType;
    const orgAdminMetadataStep = this.isProviderPortal
      ? 'Your Organisation Admin will review the metadata. '
      : '';
    if (t === 'SFTP') {
      return (
        orgAdminMetadataStep +
        "Our team will review your SFTP configuration details. You'll receive an email notification once your SFTP access is approved and set up."
      );
    }
    if (t === 'API') {
      return (
        orgAdminMetadataStep +
        "Our team will review your API integration details. You'll receive an email notification once your API integration is approved and set up."
      );
    }
    return orgAdminMetadataStep.trim();
  }

  closeNgsildUploadRequestSuccessModal(): void {
    this.ngsildUploadRequestSuccessVisible = false;
    this.ngsildUploadRequestSuccessType = null;
    this.ngsildUploadRequestSuccessRow = null;
  }

  onNgsildUploadRequestSuccessViewDataset(): void {
    const row = this.ngsildUploadRequestSuccessRow;
    this.closeNgsildUploadRequestSuccessModal();
    if (row) {
      this.viewAsset(row);
    }
  }

  getNgsildUploadRequestSuccessAssetId(): string | null {
    const row = this.ngsildUploadRequestSuccessRow;
    if (!row) return null;
    const o = row.originalData || {};
    const id =
      o.id ?? o['assetId'] ?? o['datasetId'] ?? o['modelId'];
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  onNgsildUploadModalPublishSuccess(): void {
    this.toasterService.success('Data uploaded successfully.');
    this.closeNgsildUploadModal();
    void this.loadContributions();
  }

  onNgsildUploadModalRequestSuccess(_event: { type: NgsildUploadRequestType }): void {
    this.ngsildUploadRequestSuccessType = _event.type;
    this.ngsildUploadRequestSuccessRow = this.ngsildUploadModalRow;
    this.closeNgsildUploadModal();
    this.ngsildUploadRequestSuccessVisible = true;
    this.cdr.detectChanges();
    void this.loadContributions();
  }

  onNgsildUploadModalDirectPushSuccess(): void {
    const row = this.ngsildUploadModalRow;
    if (row) {
      row.originalData = { ...(row.originalData || {}), itemStatus: 'Direct' };
    }
    this.toasterService.success('Direct Push configured.');
    this.closeNgsildUploadModal();
    void this.loadContributions();
  }

  /** Opens View Files modal (file server uploads + list). */
  openFileUploadModal(row: ContributionData): void {
    void this.openFileUploadModalAsync(row);
  }

  private async openFileUploadModalAsync(row: ContributionData): Promise<void> {
    const original = row.originalData || {};
    const assetId =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];
    if (!assetId) {
      this.toasterService.error('Unable to view files: missing asset ID');
      return;
    }

    if (this.isFileResourceServer(row)) {
      await this.ensureAssetMediaUrlLoaded(row);
      if (this.modelUsesMediaUrl(row)) {
        await this.openNgsildUploadModal(row);
        return;
      }
    }

    /** Open on the next macrotask so dropdown removal finishes before the modal mounts (same idea as ActionDropdownTriggerDirective). */
    setTimeout(() => {
      this.fileUploadModalRow = row;
      this.viewFilesTableRows = [];
      this.viewFilesListError = null;
      this.viewFilesListLoading = false;
      this.viewFilesModalTab = 'files';
      this.showFileUploadModal = true;
      this.tryBindViewFilesTableTemplates();
      void this.loadDatabankFilesForViewFilesTab();
      this.cdr.detectChanges();
    }, 0);
  }

  closeFileUploadModal(): void {
    this.closeViewFilesActionDropdown();
    this.showViewFilesDeleteConfirmDialog = false;
    this.viewFilesFilePendingDelete = null;
    this.viewFilesDeleteLoading = false;
    this.showFileUploadModal = false;
    this.fileUploadModalRow = null;
    this.viewFilesTableRows = [];
    this.viewFilesModalTab = 'files';
    this.viewFilesListLoading = false;
    this.viewFilesListError = null;
    this.cdr.detectChanges();
  }

  async openViewFeedbackModal(row: ContributionData): Promise<void> {
    const assetId = this.getRowAssetId(row);
    if (!assetId) {
      this.toasterService.error('Unable to load feedback: missing asset id');
      return;
    }

    this.viewFeedbackAssetName = row.assetName || 'this asset';
    this.viewFeedbackRows = [];
    this.viewFeedbackSummary = null;
    this.viewFeedbackError = null;
    this.viewFeedbackLoading = true;
    setTimeout(() => {
      this.showViewFeedbackModal = true;
      this.cdr.detectChanges();
    }, 0);

    try {
      const { items, summary } = await this.fetchAllFeedbackForAsset(assetId);
      this.viewFeedbackRows = items.map((item) => this.mapAssetFeedbackRow(item));
      this.viewFeedbackSummary = summary ?? this.summarizeFeedbackRows(this.viewFeedbackRows);
    } catch (error) {
      console.error('Error loading asset feedback:', error);
      this.viewFeedbackError = 'Unable to load feedback for this asset.';
      this.viewFeedbackRows = [];
      this.viewFeedbackSummary = null;
    } finally {
      this.viewFeedbackLoading = false;
      this.cdr.detectChanges();
    }
  }

  closeViewFeedbackModal(): void {
    this.showViewFeedbackModal = false;
    this.viewFeedbackAssetName = '';
    this.viewFeedbackRows = [];
    this.viewFeedbackSummary = null;
    this.viewFeedbackError = null;
    this.viewFeedbackLoading = false;
    this.cdr.detectChanges();
  }

  get feedbackDetailsTitle(): string {
    const name = this.viewFeedbackAssetName?.trim();
    return name ? `Feedback details for ${name}` : 'Feedback details';
  }

  feedbackStarFilled(ratingValue: number, star: number): boolean {
    return star <= ratingValue;
  }

  summaryStarFillPercent(star: number): number {
    return ratingStarFraction(star, this.viewFeedbackSummary?.averageRating) * 100;
  }

  private async fetchAllFeedbackForAsset(
    assetId: string,
  ): Promise<{ items: unknown[]; summary: AssetFeedbackSummary | null }> {
    const pageSize = 100;
    const items: unknown[] = [];
    let page = 1;
    let total = Number.POSITIVE_INFINITY;
    let summary: AssetFeedbackSummary | null = null;

    while (items.length < total && page <= 50) {
      const response = await this.userInteractionApiService.getApprovedUserFeedback({
        assetId,
        page,
        size: pageSize,
      });
      if (!summary) {
        summary = this.extractFeedbackSummary(response);
      }
      const pageItems = this.extractFeedbackList(response);
      total = this.extractFeedbackTotal(response, items.length + pageItems.length);
      items.push(...pageItems);
      if (pageItems.length === 0) {
        break;
      }
      page += 1;
    }

    return { items, summary };
  }

  private collapseRepeatedFeedbackName(value: string): string {
    const text = value.trim();
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
      return parts[0];
    }
    return text;
  }

  private mapAssetFeedbackRow(item: unknown): AssetFeedbackRow {
    const source = this.asFeedbackRecord(item);
    const user = this.asFeedbackRecord(source['user'] ?? source['consumer'] ?? source['createdBy']);
    const comment =
      this.readFeedbackString(source, ['comment', 'feedback', 'userFeedback', 'review']) ||
      this.readFeedbackCommentFromSubdata(source['actionSubdata']);
    const createdAtRaw = this.readFeedbackString(source, [
      'ratingCreatedAt',
      'ratingUpdatedAt',
      'createdAt',
      'created_at',
      'updatedAt',
      'updated_at',
      'submittedAt',
    ]);
    const consumerEmail =
      this.readFeedbackString(source, ['userEmail', 'consumerEmail', 'email']) ||
      this.readFeedbackString(user, ['email', 'userEmail']) ||
      '';
    const consumerName = this.collapseRepeatedFeedbackName(
      this.readFeedbackString(user, ['fullName', 'name', 'userName']) ||
        this.readFeedbackString(source, ['consumerName', 'userName', 'user_name', 'fullName']) ||
        consumerEmail ||
        'N/A',
    );
    const ratingValue = this.readFeedbackRatingValue(source);

    return {
      consumerName,
      consumerEmail: consumerEmail || 'N/A',
      organisation:
        this.readFeedbackString(source, ['orgName', 'organisationName', 'organisation', 'organization']) ||
        this.readFeedbackString(user, ['orgName', 'organisationName', 'organisation', 'organization']),
      userFeedback: comment || 'No written feedback',
      rating: ratingValue != null ? String(ratingValue) : '—',
      ratingValue: ratingValue ?? 0,
      createdAt: createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '—',
    };
  }

  private extractFeedbackList(response: unknown): unknown[] {
    const record = this.asFeedbackRecord(response);
    if (Array.isArray(record['result'])) {
      return record['result'];
    }
    const nestedResult = this.asFeedbackRecord(record['result']);
    if (Array.isArray(nestedResult['data'])) {
      return nestedResult['data'];
    }
    if (Array.isArray(record['results'])) {
      return record['results'];
    }
    if (Array.isArray(record['data'])) {
      return record['data'];
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private extractFeedbackSummary(response: unknown): AssetFeedbackSummary | null {
    const record = this.asFeedbackRecord(response);
    const nestedResult = this.asFeedbackRecord(record['result']);
    const summary = this.asFeedbackRecord(nestedResult['summary'] ?? record['summary']);
    const averageRaw = summary['averageRating'] ?? summary['average_rating'];
    const totalRaw = summary['totalRatings'] ?? summary['total_ratings'] ?? summary['totalCount'];
    const averageRating = Number(averageRaw);
    const totalRatings = Number(totalRaw);
    if (!Number.isFinite(averageRating) && !Number.isFinite(totalRatings)) {
      return null;
    }
    return {
      averageRating: Number.isFinite(averageRating) ? averageRating : null,
      totalRatings: Number.isFinite(totalRatings) ? totalRatings : 0,
    };
  }

  private summarizeFeedbackRows(rows: AssetFeedbackRow[]): AssetFeedbackSummary | null {
    if (!rows.length) {
      return null;
    }
    const rated = rows.filter((row) => row.ratingValue > 0);
    const total = rated.reduce((sum, row) => sum + row.ratingValue, 0);
    return {
      averageRating: rated.length ? total / rated.length : null,
      totalRatings: rated.length,
    };
  }

  private extractFeedbackTotal(response: unknown, fallback: number): number {
    const record = this.asFeedbackRecord(response);
    const paginationInfo = this.asFeedbackRecord(record['paginationInfo']);
    const pagination = this.asFeedbackRecord(record['pagination']);
    const total =
      paginationInfo['totalCount'] ??
      pagination['totalCount'] ??
      record['totalCount'] ??
      record['total'];
    const numeric = Number(total);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  private readFeedbackRatingValue(source: Record<string, unknown>): number | null {
    const value = source['entityRating'] ?? source['rating'] ?? source['stars'];
    if (value == null || value === '') {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private readFeedbackCommentFromSubdata(value: unknown): string {
    if (typeof value === 'string') {
      try {
        return this.readFeedbackCommentFromSubdata(JSON.parse(value) as unknown);
      } catch {
        return value.trim();
      }
    }
    const record = this.asFeedbackRecord(value);
    return this.readFeedbackString(record, ['comment', 'feedback', 'text', 'message']);
  }

  private readFeedbackString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private asFeedbackRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  setViewFilesModalTab(tab: ViewFilesModalTab): void {
    this.closeViewFilesActionDropdown();
    this.viewFilesModalTab = tab;
    this.tryBindViewFilesTableTemplates();
    if (tab === 'files') {
      void this.loadDatabankFilesForViewFilesTab();
    }
  }

  /**
   * Lists databank files from the file server (`POST /v1/databanks/{id}/files` — same path as UI “list files”).
   */
  private async loadDatabankFilesForViewFilesTab(): Promise<void> {
    const id = this.getFileUploadModalItemId();
    if (!id) {
      this.viewFilesListError = 'Missing dataset ID.';
      this.viewFilesTableRows = [];
      this.cdr.detectChanges();
      return;
    }

    if (this.viewFilesListLoading) {
      return;
    }

    this.viewFilesListLoading = true;
    this.viewFilesListError = null;
    this.cdr.detectChanges();

    try {
      const res = await this.fileApiService.listFiles(id, 1000, true);
      const raw = this.extractDatabankFilesFromListResponse(res?.data);
      this.viewFilesTableRows = raw
        .filter((f) => f['isFile'] !== false)
        .map((f, i) => this.mapApiFileToDatasetRow(f, i));
    } catch (e: unknown) {
      console.error('loadDatabankFilesForViewFilesTab:', e);
      this.viewFilesListError =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message ?? 'Unknown error')
          : 'Failed to load files.';
      this.viewFilesTableRows = [];
      this.toasterService.error('Failed to load files from file server.');
    } finally {
      this.viewFilesListLoading = false;
      this.cdr.detectChanges();
    }
  }

  /** Unwrap `files` from list-files / preview payloads. */
  private extractDatabankFilesFromListResponse(body: unknown): Record<string, unknown>[] {
    if (!body || typeof body !== 'object') return [];
    const o = body as Record<string, unknown>;
    if (Array.isArray(o['files'])) return o['files'] as Record<string, unknown>[];
    if (o['success'] === true && o['data'] != null && typeof o['data'] === 'object') {
      const d = o['data'] as Record<string, unknown>;
      if (Array.isArray(d['files'])) return d['files'] as Record<string, unknown>[];
    }
    const nested = o['data'];
    if (
      nested != null &&
      typeof nested === 'object' &&
      Array.isArray((nested as Record<string, unknown>)['files'])
    ) {
      return (nested as Record<string, unknown>)['files'] as Record<string, unknown>[];
    }
    return [];
  }

  private mapApiFileToDatasetRow(item: Record<string, unknown>, index: number): DatasetFileRow {
    const key = String(item['key'] ?? item['name'] ?? '').trim() || `file-${index + 1}`;
    const sizeRaw = item['size'];
    const bytes =
      typeof sizeRaw === 'number' && !isNaN(sizeRaw)
        ? sizeRaw
        : parseInt(String(sizeRaw ?? ''), 10);
    const lm = item['lastModified'] ?? item['last_modified'];
    let uploadedOn = '—';
    if (lm != null && String(lm).trim()) {
      const d = new Date(String(lm));
      if (!isNaN(d.getTime())) {
        uploadedOn = this.formatPublishedOn(d.toISOString());
      }
    }
    const status = String(item['status'] ?? 'Ready').trim() || 'Ready';
    return {
      id: key,
      fileName: key,
      size: !isNaN(bytes) && bytes > 0 ? this.formatFileSizeBytes(bytes) : '—',
      uploadedOn,
      status,
    };
  }

  private formatFileSizeBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  /**
   * Single-file download: POST `…/files/download` with presigned URL (same catalogue flow as View File PDF),
   * then blob download if presign is unavailable — uses HttpClient auth (Bearer from login).
   */
  async onDownloadDatasetFile(file: DatasetFileRow): Promise<void> {
    const databankId = this.getFileUploadModalItemId();
    const key = String(file.id || file.fileName || '').trim();
    if (!databankId || !key) {
      this.toasterService.error('Cannot download file: missing dataset ID or file key.');
      return;
    }
    let loggedIn = false;
    try {
      loggedIn = await this.keycloak.isLoggedIn();
    } catch {
      loggedIn = !!this.keycloak.getKeycloakInstance()?.token;
    }
    if (!loggedIn) {
      this.toasterService.error('Please sign in to download files.');
      return;
    }

    try {
      const { presignedUrl } = await this.fileApiService.getPresignedDownloadUrl(databankId, { key, presigned: true });
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = file.fileName || key;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toasterService.success('Download started successfully');
    } catch (presignErr: unknown) {
      console.warn('Presigned file download failed; falling back to authenticated blob download', presignErr);
      try {
        const blob = await this.fileApiService.getFileBlob(databankId, { key, presigned: false });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.fileName || key;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
        this.toasterService.success('Download started successfully');
      } catch (e: unknown) {
        console.error('Download file:', e);
        this.toasterService.error('Failed to download file.');
      }
    }
  }

  openViewFilesDeleteConfirmDialog(file: DatasetFileRow): void {
    const databankId = this.getFileUploadModalItemId();
    const key = String(file.id || file.fileName || '').trim();
    if (!databankId || !key) {
      this.toasterService.error('Cannot delete file: missing dataset ID or file key.');
      return;
    }
    this.viewFilesFilePendingDelete = file;
    this.showViewFilesDeleteConfirmDialog = true;
    this.cdr.markForCheck();
  }

  onViewFilesDeleteDialogVisibleChange(visible: boolean): void {
    this.showViewFilesDeleteConfirmDialog = visible;
    if (!visible && !this.viewFilesDeleteLoading) {
      this.viewFilesFilePendingDelete = null;
    }
    this.cdr.markForCheck();
  }

  onCancelViewFilesDeleteDialog(): void {
    this.showViewFilesDeleteConfirmDialog = false;
    this.viewFilesFilePendingDelete = null;
  }

  getViewFilesDeleteConfirmMessage(): string {
    const f = this.viewFilesFilePendingDelete;
    if (!f?.fileName) {
      return 'Are you sure you want to delete this file from this dataset? This action cannot be undone.';
    }
    const safe = this.escapeForInlineHtml(String(f.fileName));
    return `Are you sure you want to delete &quot;<strong>${safe}</strong>&quot; from this dataset? This action cannot be undone.`;
  }

  private escapeForInlineHtml(raw: string): string {
    return raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async onConfirmDeleteViewFilesFile(): Promise<void> {
    const file = this.viewFilesFilePendingDelete;
    if (!file) {
      return;
    }
    const databankId = this.getFileUploadModalItemId();
    const key = String(file.id || file.fileName || '').trim();
    if (!databankId || !key) {
      this.toasterService.error('Cannot delete file: missing dataset ID or file key.');
      this.showViewFilesDeleteConfirmDialog = false;
      this.viewFilesFilePendingDelete = null;
      return;
    }

    this.viewFilesDeleteLoading = true;
    this.cdr.markForCheck();
    let deleted = false;
    try {
      await this.fileApiService.deleteFile(databankId, key);
      deleted = true;
      await this.processViewFilesDatabank(databankId);
      this.toasterService.success(`"${file.fileName}" was deleted.`);
      this.showViewFilesDeleteConfirmDialog = false;
      this.viewFilesFilePendingDelete = null;
      await this.loadDatabankFilesForViewFilesTab();
      void this.loadContributions();
    } catch (e: unknown) {
      let msg = deleted
        ? 'File was deleted, but processing the dataset failed.'
        : 'Could not delete the file.';
      if (!deleted && e && typeof e === 'object') {
        const err = e as { message?: string; error?: { message?: string } };
        if (err.error?.message) {
          msg = String(err.error.message);
        } else if (err.message) {
          msg = String(err.message);
        }
      }
      this.toasterService.error(msg);
      console.error(deleted ? 'processDatabank after delete:' : 'deleteFile:', e);
      if (deleted) {
        this.showViewFilesDeleteConfirmDialog = false;
        this.viewFilesFilePendingDelete = null;
        await this.loadDatabankFilesForViewFilesTab();
        void this.loadContributions();
      }
    } finally {
      this.viewFilesDeleteLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Reprocesses this dataset on the file server. */
  private async processViewFilesDatabank(databankId: string): Promise<void> {
    const response = await this.fileApiService.processDatabank(databankId, 'all');
    if (response.status !== 200 && response.status !== 202) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  }

  getFileUploadModalItemId(): string | null {
    const original = this.fileUploadModalRow?.originalData || {};
    const id =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  getFileUploadModalResourceServer(): ResourceServerId {
    return 'file-server';
  }

  getFileUploadModalFileFormat(): string {
    const original = this.fileUploadModalRow?.originalData || {};
    const fmt = original.fileFormat ?? original.file_format ?? 'csv';
    return String(fmt).trim() || 'csv';
  }

  onFileUploadPublishSuccess(): void {
    this.toasterService.success('Files uploaded successfully');
    this.viewFilesModalTab = 'files';
    void this.loadDatabankFilesForViewFilesTab();
    void this.loadContributions();
  }

  /** Open upload UI for post-approval file uploads (models use connection-details modal). */
  private navigateToUploadMore(row: ContributionData): void {
    const original = row.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];
    if (!assetId) {
      this.toasterService.error('Unable to upload files: missing asset ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    if (typeLabel === 'model' || this.isModelAsset(row)) {
      void this.openNgsildUploadModal(row);
      return;
    }

    const base = this.manageAssetsPortal === 'provider' ? '/provider/add-contributions' : '/org-admin';
    const uploadsPath = `${base}/add-dataset/uploads`;
    void this.router.navigate([uploadsPath], { queryParams: { assetId: String(assetId) } });
  }

  /** View asset in new tab (opens asset detail page). */
  private viewAsset(row: ContributionData): void {
    const original = row.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];

    if (!assetId) {
      console.warn('Unable to navigate to asset details: missing asset id', row);
      this.toasterService.error('Unable to view asset: missing asset ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType = original.type || original.assetType || original.asset_type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();
      
      if (rawTypeLower.includes('databank') || rawTypeLower.includes('database')) {
        routeSegment = 'dataset';
      } else if (rawTypeLower.includes('aimodel') || rawTypeLower.includes('model')) {
        routeSegment = 'model';
      } else if (rawTypeLower.includes('app') || rawTypeLower.includes('usecase')) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      // Open in new tab
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, assetId])
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error('Unable to determine asset type for navigation');
    }
  }

  /** Catalogue item id for a table row. */
  getRowAssetId(row: ContributionData | null | undefined): string | null {
    if (!row) return null;
    const original = row.originalData || {};
    const id =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];
    return id != null && id !== '' ? String(id) : null;
  }

  /** Resolve dataset vs model from API type (preferred) or table display label. */
  private resolveAssetRouteType(row: ContributionData | null | undefined): 'dataset' | 'model' | null {
    if (!row) return null;
    const route = this.getRoutePathFromCatalogueType(row.originalData?.type);
    if (route === 'model' || route === 'dataset') {
      return route;
    }
    const label = (row.assetType || '').toLowerCase();
    if (label.includes('model')) return 'model';
    if (label.includes('dataset')) return 'dataset';
    return null;
  }

  /** Map catalogue type (e.g. adex:DataBank) to route segment. */
  private getRoutePathFromCatalogueType(type: string[] | string | undefined): string {
    if (!type) return 'dataset';
    let typeString = '';
    if (Array.isArray(type) && type.length > 0) {
      typeString = type[0];
    } else if (typeof type === 'string') {
      typeString = type;
    } else {
      return 'dataset';
    }
    const parts = typeString.split(':');
    const typeName = parts.length > 1 ? parts[parts.length - 1] : typeString;
    const normalized = typeName.toLowerCase();
    if (normalized === 'aimodel' || normalized === 'ai_model' || normalized === 'ai model') {
      return 'model';
    }
    if (normalized === 'databank' || normalized === 'data_bank') {
      return 'dataset';
    }
    if (normalized === 'apps' || normalized === 'app' || normalized === 'usecase') {
      return 'usecase';
    }
    return 'dataset';
  }

  /** Open Modify Asset modal and load asset data (same flow as Access Request page). */
  async openModifyModal(row: ContributionData): Promise<void> {
    const assetId = this.getRowAssetId(row);
    const routeType = this.resolveAssetRouteType(row);

    if (!assetId) {
      console.warn('Unable to modify asset: missing asset id', row);
      this.toasterService.error('Unable to modify asset: missing asset ID');
      return;
    }

    if (routeType !== 'dataset' && routeType !== 'model') {
      this.toasterService.error('Modify metadata is not available for this asset type');
      return;
    }

    const loadToken = ++this.modifyAssetLoadToken;
    this.currentModifyAsset = row;
    this.isLoadingAssetData = true;
    this.modifyAssetData = null;
    this.modifyFormInstanceKey = null;
    this.showModifyModal = true;

    try {
      const response = await this.catalogueApiService.getItemById(assetId);
      if (loadToken !== this.modifyAssetLoadToken) {
        return;
      }

      let assetData = response;
      if (response?.result) {
        assetData = Array.isArray(response.result) ? response.result[0] : response.result;
      } else if (response?.data) {
        assetData = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        assetData = response[0];
      }

      if (!assetData) {
        throw new Error('No asset data found in response');
      }

      this.currentModifyAsset = {
        ...row,
        originalData: {
          ...(row.originalData || {}),
          ...assetData,
        },
      };

      const typeLabel = routeType === 'model' ? 'Model' : 'Dataset';
      this.modifyAssetData = this.mapAssetDataToPreviewFormat(assetData, typeLabel);
      this.modifyFormInstanceKey = `${assetId}-${++this.modifyFormInstanceSeq}`;
    } catch (error) {
      console.error('Error loading asset data for modification:', error);
      if (loadToken === this.modifyAssetLoadToken) {
        this.toasterService.error('Failed to load asset data. Please try again.');
        this.closeModifyModal();
      }
    } finally {
      if (loadToken === this.modifyAssetLoadToken) {
        this.isLoadingAssetData = false;
        this.cdr.detectChanges();
      }
    }
  }

  closeModifyModal(): void {
    this.modifyAssetLoadToken++;
    this.showModifyModal = false;
    this.currentModifyAsset = null;
    this.modifyAssetData = null;
    this.modifyFormInstanceKey = null;
    this.isLoadingAssetData = false;
  }

  getAssetTypeForModify(): 'dataset' | 'model' | null {
    return this.resolveAssetRouteType(this.currentModifyAsset);
  }

  /** Ensure string meets preview-metadata min length (50 chars) so form is valid and Save can submit. */
  private ensureMinLength(str: string, min: number): string {
    const s = (str || '').trim();
    return s.length >= min ? s : s + ' '.repeat(Math.max(0, min - s.length));
  }

  /** Map API permission / accessPolicy to modify-form values (Open | Restricted). */
  private normalizePreviewPermission(raw: unknown): 'Open' | 'Restricted' {
    if (raw === 'Open' || raw === 'Restricted') {
      return raw;
    }
    const upper = String(raw ?? '').trim().toUpperCase();
    if (upper === 'RESTRICTED' || upper === 'PRIVATE') {
      return 'Restricted';
    }
    if (upper === 'OPEN' || upper === 'PUBLIC') {
      return 'Open';
    }
    return 'Open';
  }

  /** Catalogue placeholder media URLs — not real data sources. */
  private isPlaceholderMediaUrl(url: unknown): boolean {
    const normalized = String(url ?? '').trim().toLowerCase().replace(/\/+$/, '');
    return !normalized || normalized === 'https://iudx.io' || normalized === 'http://iudx.io';
  }

  private normalizeMediaUrlForDisplay(url: unknown): string {
    return this.isPlaceholderMediaUrl(url) ? '' : String(url ?? '').trim();
  }

  /** Map API response to preview-metadata expected format (same as publish-assets). */
  private mapAssetDataToPreviewFormat(apiData: any, assetType: string): any {
    const isModel = assetType.toLowerCase().includes('model') ||
                    (apiData.type && Array.isArray(apiData.type) && apiData.type.some((t: string) => t && (t + '').includes('AiModel'))) ||
                    (apiData.type && typeof apiData.type === 'string' && (apiData.type + '').includes('AiModel'));

    const accessPolicy = apiData.accessPolicy || apiData.access_policy || 'OPEN';
    const permission = this.normalizePreviewPermission(
      apiData.permission ?? accessPolicy
    );

    let yearRange = apiData.yearRange || apiData.year_range || null;
    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0], 10) || null,
          endYear: parseInt(parts[1], 10) || null
        };
      }
    }

    const rawShort = apiData.shortDescription || apiData.short_description || '';
    const rawLong = apiData.description || apiData.longDescription || '';
    const shortDescription = this.ensureMinLength(rawShort, 50);
    const longDescription = this.ensureMinLength(rawLong, 50);

    return {
      name: apiData.name || apiData.label || '',
      title: apiData.name || apiData.label || '',
      permission: permission,
      accessPolicy: accessPolicy,
      shortDescription,
      description: longDescription,
      longDescription,
      license: apiData.license || 'Open Government License, India',
      otherLicense: apiData.otherLicense || apiData.other_license || '',
      resourceServer: apiData.resourceServer || apiData.resource_server || '',
      uploadFrequency: apiData.uploadFrequency || apiData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: apiData.geoCoverage || apiData.geo_coverage || '',
      industry: apiData.industry || 'Agriculture',
      datasetType: isModel
        ? (apiData.modelType || apiData.model_type || apiData.datasetType || '')
        : (apiData.dataBankType || apiData.datasetType || 'Structured'),
      fileFormat: apiData.fileFormat || apiData.file_format || 'CSV',
      tags: Array.isArray(apiData.tags) ? apiData.tags : (apiData.tags ? [apiData.tags] : []),
      sampleDataUrl: this.normalizeMediaUrlForDisplay(
        apiData.mediaURL || apiData.media_url || apiData.mediaUrl || apiData.sampleDataUrl || ''
      ),
      dataDescriptor: apiData.dataDescriptor ?? apiData.data_descriptor,
      dataDescriptors: normalizeAssetDescriptorList(
        apiData.dataDescriptor ?? apiData.data_descriptor ?? apiData.dataDescriptors ?? [],
      ),
      type: apiData.type || []
    };
  }

  async onModifyFormSubmit(formData: any): Promise<void> {
    if (!this.currentModifyAsset) return;

    const assetId = this.getRowAssetId(this.currentModifyAsset);
    if (!assetId) {
      this.toasterService.error('Unable to update asset: missing asset ID');
      return;
    }

    try {
      const payload = await buildAssetCataloguePayloadWithAuth(this.authApiService, {
        formData,
        isModel: this.getAssetTypeForModify() === 'model',
        assetId,
        originalData: this.currentModifyAsset?.originalData,
        assetPreviewData: this.modifyAssetData ?? undefined,
        dataDescriptors: resolveModifyAssetDescriptors(formData, {
          assetPreviewData: this.modifyAssetData ?? undefined,
          originalData: this.currentModifyAsset?.originalData,
        }),
        includeModelDataSourceUrl: true,
      });
      this.applyItemStatusFromOriginalData(payload, this.currentModifyAsset?.originalData);
      await this.catalogueApiService.updateItem(payload);

      this.applyModifyResultToTableRow(assetId, formData, payload);
      this.toasterService.success('Asset metadata updated successfully');
      this.closeModifyModal();
      // Search index can lag; delay reload so optimistic row update is not overwritten by stale data.
      setTimeout(() => void this.loadContributions(), 800);
    } catch (error) {
      console.error('Error updating asset:', error);
      this.toasterService.error('Failed to update asset. Please try again.');
    }
  }

  /** Reflect modify-modal saves in the table immediately (permission, name, etc.). */
  private applyModifyResultToTableRow(
    assetId: string,
    formData: Record<string, unknown>,
    payload: Record<string, unknown>,
  ): void {
    const permissionRaw =
      payload['accessPolicy'] ??
      payload['access_policy'] ??
      formData['permission'] ??
      'OPEN';
    const assetName = String(formData['title'] ?? '').trim();

    this.contributionsData = this.contributionsData.map((row) => {
      if (this.getRowAssetId(row) !== assetId) {
        return row;
      }
      const originalData = { ...(row.originalData || {}) };
      if (assetName) {
        originalData['name'] = assetName;
        originalData['title'] = assetName;
      }
      originalData['accessPolicy'] = permissionRaw;
      originalData['access_policy'] = permissionRaw;
      originalData['permission'] = permissionRaw;

      return {
        ...row,
        assetName: assetName || row.assetName,
        permission: String(permissionRaw),
        originalData,
      };
    });
    this.cdr.detectChanges();
  }

  private buildUpdatePayload(formData: any, assetId: string): any {
    const isModel = this.getAssetTypeForModify() === 'model';
    const originalData = this.currentModifyAsset?.originalData || {};

    const rawType = originalData.type;
    const typeArray = Array.isArray(rawType) ? rawType : (rawType ? [rawType] : []);

    const payload: any = {
      id: assetId,
      type: typeArray.length > 0 ? typeArray : (isModel ? ['adex:AiModel'] : ['adex:DataBank']),
      name: formData.title || originalData.name,
      description: (formData.longDescription || formData.description || '').trim(),
      shortDescription: (formData.shortDescription || '').trim(),
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      license: formData.license || '',
      industry: formData.industry || '',
      organizationId: originalData.organizationId,
      organizationType: originalData.organizationType,
      uploadedBy: originalData.uploadedBy,
      organization: originalData.organization,
      department: originalData.department
    };

    if (isModel) {
      payload.modelType = formData.datasetType || originalData.modelType;
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.fileSize = originalData.fileSize || '';
      const dataSourceUrl = this.normalizeMediaUrlForDisplay(formData.sampleDataUrl ?? '');
      if (dataSourceUrl) {
        payload.mediaURL = dataSourceUrl;
      } else {
        const existingUrl = this.normalizeMediaUrlForDisplay(
          originalData.mediaURL ?? originalData.media_url ?? ''
        );
        if (existingUrl) {
          payload.mediaURL = existingUrl;
        }
      }
    } else {
      payload.geoCoverage = formData.geoCoverage || '';
      payload.yearRange = this.formatYearRangeAsString(formData.yearRange);
      payload.resourceServer = this.buildResourceServerArray(formData.resourceServer || '');
      payload.uploadFrequency = formData.uploadFrequency || '';
      payload.dataBankType = formData.datasetType || '';
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.dataDescriptor = this.normalizeDataDescriptorToObject(formData.dataDescriptors);
      const dataSourceUrl = this.normalizeMediaUrlForDisplay(formData.sampleDataUrl ?? '');
      if (dataSourceUrl) {
        payload.mediaURL = dataSourceUrl;
      } else {
        const existingUrl = this.normalizeMediaUrlForDisplay(
          originalData.mediaURL ?? originalData.media_url ?? ''
        );
        if (existingUrl) {
          payload.mediaURL = existingUrl;
        }
      }
    }

    const permission = formData.permission || 'Open';
    payload.accessPolicy = permission === 'Open' ? 'OPEN' :
                           permission === 'Restricted' ? 'RESTRICTED' : 'PRIVATE';

    this.appendPreservedAssetMetadata(payload, originalData);

    return payload;
  }

  /** Preserve catalogue metadata from getItemById when updating an asset. */
  private appendPreservedAssetMetadata(payload: Record<string, any>, source: Record<string, any>): void {
    const itemCreatedAt =
      source['itemCreatedAt'] ?? source['item_created_at'] ?? source['createdAt'] ?? source['created_at'];
    if (itemCreatedAt !== undefined && itemCreatedAt !== null && itemCreatedAt !== '') {
      payload['itemCreatedAt'] = itemCreatedAt;
    }

    const ownerUserId = source['ownerUserId'] ?? source['owner_user_id'];
    if (ownerUserId !== undefined && ownerUserId !== null && ownerUserId !== '') {
      payload['ownerUserId'] = ownerUserId;
    }

    if (source['metrics'] !== undefined && source['metrics'] !== null) {
      payload['metrics'] = source['metrics'];
    }

    this.applyItemStatusFromOriginalData(payload, source);

    const publishStatus = source['publishStatus'] ?? source['publish_status'];
    if (publishStatus !== undefined && publishStatus !== null && publishStatus !== '') {
      payload['publishStatus'] = publishStatus;
    }

    if (source['dataUploadStatus'] !== undefined || source['data_upload_status'] !== undefined) {
      payload['dataUploadStatus'] = source['dataUploadStatus'] ?? source['data_upload_status'];
    }
  }

  /** Keep catalogue `itemStatus` from getItemById / row originalData on PUT modify. */
  private applyItemStatusFromOriginalData(
    payload: Record<string, unknown>,
    source: Record<string, unknown> | null | undefined,
  ): void {
    if (!source) {
      return;
    }
    const itemStatus = source['itemStatus'] ?? source['item_status'];
    if (itemStatus !== undefined && itemStatus !== null && String(itemStatus).trim() !== '') {
      payload['itemStatus'] = String(itemStatus).trim();
    }
  }

  /** yearRange for update API: string format "2020-2021" (API expects string). */
  private formatYearRangeAsString(yearRange: any): string | null {
    if (yearRange == null) return null;
    if (typeof yearRange === 'string') return yearRange.trim() || null;
    if (typeof yearRange === 'object' && yearRange.startYear != null && yearRange.endYear != null) {
      return `${yearRange.startYear}-${yearRange.endYear}`;
    }
    return null;
  }

  private normalizeDataDescriptorToObject(dataDescriptors: any): Record<string, any> {
    if (dataDescriptors && typeof dataDescriptors === 'object' && !Array.isArray(dataDescriptors)) {
      return dataDescriptors;
    }
    const list = Array.isArray(dataDescriptors) ? dataDescriptors : [];
    const obj: Record<string, any> = {};
    list.forEach((desc: any) => {
      if (desc && typeof desc === 'object' && desc.attributeName) {
        const entry: Record<string, string> = {
          parameterType: desc.parameterType || '',
          schemaType: desc.schemaType || '',
        };
        const description = String(desc.description ?? '').trim();
        if (description) {
          entry['description'] = description;
        }
        obj[desc.attributeName] = entry;
      }
    });
    return obj;
  }

  private buildResourceServerArray(urlFromDropdown: string): any[] {
    const defaultId = 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1';
    const base = {
      id: defaultId,
      queryTypes: ['ATTR'],
      accessTypes: ['ATTR'],
      url: urlFromDropdown || ''
    };
    if (!urlFromDropdown || typeof urlFromDropdown !== 'string') {
      return [{ ...base, name: 'FILE', type: 'file' }];
    }
    const u = urlFromDropdown.toLowerCase();
    if (u.includes('ogc')) return [{ ...base, name: 'OGC', type: 'ogc' }];
    if (u.includes('rs.iudx') || u.includes('ngsi')) return [{ ...base, name: 'NGSI-LD', type: 'ngsi-ld' }];
    if (u.includes('rs-proxy') || u.includes('gateway')) return [{ ...base, name: 'GATEWAY', type: 'gateway' }];
    return [{ ...base, name: 'FILE', type: 'file' }];
  }

  onModifyFormCancel(): void {
    this.closeModifyModal();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
    if (
      this.viewFilesOpenDropdownKey !== null &&
      !target.closest('.view-files-action-cell-wrapper') &&
      !target.closest('.view-files-action-dropdown-overlay')
    ) {
      this.closeViewFilesActionDropdown();
    }
  }

}


